/**
 * PhysicalMeetingPhase V2 - Complete New Implementation
 * 
 * Features:
 * - Real-time location sharing (both users on map)
 * - User location proposals (minimaal 3 per user, saved per user)
 * - Best match calculation (from all 6 proposals, shows top 3)
 * - Quick location picker (editable/removable, saved locations)
 * - Navigate to location (route + travel time, reset, GPS verification)
 * - On-the-way notification system (popup at user 2)
 * - AI Relation Manager (Spar/Skip after End Meeting)
 * - LLM Expertstatus script generation
 * 
 * State Machine:
 * planning → scheduled → navigation → active → completed → ai_relation_manager → done
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { 
  doc, 
  updateDoc, 
  getDoc, 
  setDoc,
  serverTimestamp, 
  collection, 
  addDoc, 
  getDocs,
  query,
  where,
  orderBy,
  limit as fsLimit,
  onSnapshot,
  deleteField,
  runTransaction
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { db } from '../../firebase';
import EnhancedLocationMap from '../maps/EnhancedLocationMap';
import { 
  geocodeAddress, 
  reverseGeocode,
  GeocodingResult 
} from '../../services/geocodingService';
import { 
  calculateRoute, 
  calculateBestMatches,
  BestMatch 
} from '../../services/routingService';
import { getUserOfficeLocation } from '../../services/locationSuggestionService';
import { useToast, ToastContainer } from '../ui/Toast';
import { useDebounce } from '../../utils/debounce';
import { validateField, ValidationRule } from '../../utils/formValidation';
import { useGPSPermission } from '../../hooks/useGPSPermission';
import { updatePresence } from '../../services/presenceService';
import { useLiveLocation } from '../../hooks/useLiveLocation';
import { locationSocket } from '../../services/locationSocket';
import { Brain, Sparkle } from 'phosphor-react';

// Types
type PhysicalStatus = 
  | 'planning'
  | 'scheduled'
  | 'navigation'
  | 'active'
  | 'completed'
  | 'ai_relation_manager'
  | 'done';

interface LocationProposal {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  distanceFromUser1?: number;
  travelTimeUser1?: number;
  distanceFromUser2?: number;
  travelTimeUser2?: number;
  proposedAt: Date;
}

interface UserLocation {
  lat: number;
  lng: number;
  address: string;
  lastUpdated: Date;
  sharingEnabled: boolean;
}

interface NavigationStatus {
  started: boolean;
  startedAt?: Date;
  arrived: boolean;
  arrivedAt?: Date;
  gpsVerified: boolean;
  distanceFromLocation: number;
}

interface OnTheWayStatus {
  user1OnTheWay: boolean;
  user1OnTheWayAt?: Date;
  user1Cancelled?: boolean;
  user2OnTheWay: boolean;
  user2OnTheWayAt?: Date;
  user2Cancelled: boolean;
  cancellationReason?: string;
}

interface AIRelationManagerData {
  completed: boolean;
  skipped: boolean;
  answers?: any;
  completedAt?: Date;
}

interface PhysicalMeetingPhaseProps {
  connectionId: string;
  currentUserId: string;
  currentUserName: string;
  otherUserId: string;
  otherUserName: string;
  currentUserProfile?: any;
  otherUserProfile?: any;
  /** Callback to open the ExtendedAgenda for scheduling date/time */
  onOpenAgenda?: () => void;
}

const PhysicalMeetingPhaseV2: React.FC<PhysicalMeetingPhaseProps> = ({
  connectionId,
  currentUserId,
  currentUserName,
  otherUserId,
  otherUserName,
  currentUserProfile,
  otherUserProfile,
  onOpenAgenda,
}) => {
  // Main state
  const [status, setStatus] = useState<PhysicalStatus>('planning');
  
  // Planning tab state
  const [planningTab, setPlanningTab] = useState<'locaties' | 'kaart' | 'matches'>('locaties');
  
  // Preferred/voted locations (locations users "liked" from each other)
  const [preferredLocationIds, setPreferredLocationIds] = useState<Set<string>>(new Set());
  const [otherPreferredIds, setOtherPreferredIds] = useState<Set<string>>(new Set());
  
  // Toast notifications
  const { toasts, success, error: showError, warning, info, removeToast } = useToast();
  
  // GPS Permission
  const { permissionStatus, requestPermission, isSupported: gpsSupported, error: gpsError } = useGPSPermission();
  
  // Specific loading states (UX Best Practice)
  const [loadingStates, setLoadingStates] = useState({
    searchingLocation: false,
    addingProposal: false,
    calculatingMatches: false,
    verifyingGPS: false,
    startingNavigation: false,
    startingMeeting: false,
    endingMeeting: false,
    submittingSpar: false,
  });
  
  const setLoading = (key: keyof typeof loadingStates, value: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };
  
  // Location proposals (minimaal 3 per user)
  const [user1Proposals, setUser1Proposals] = useState<LocationProposal[]>([]);
  const [user2Proposals, setUser2Proposals] = useState<LocationProposal[]>([]);
  const [currentUserProposals, setCurrentUserProposals] = useState<LocationProposal[]>([]);
  const [otherUserProposals, setOtherUserProposals] = useState<LocationProposal[]>([]);
  
  // Best matches (top 3)
  const [bestMatches, setBestMatches] = useState<BestMatch[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<BestMatch | null>(null);
  
  // Track if the other user is currently planning (selected location + opened agenda)
  const [otherUserIsPlanning, setOtherUserIsPlanning] = useState<{ isPlanning: boolean; userName: string; locationName: string }>({ isPlanning: false, userName: '', locationName: '' });

  // Confirmed appointment datetime (for 24h navigation lock)
  const [appointmentDateTime, setAppointmentDateTime] = useState<Date | null>(null);
  // Live countdown for the navigation lock
  
  // Real-time location sharing
  const [userLocations, setUserLocations] = useState<{
    user1: UserLocation | null;
    user2: UserLocation | null;
  }>({
    user1: null,
    user2: null
  });
  const [locationSharingEnabled, setLocationSharingEnabled] = useState(false);
  const locationWatchIdRef = useRef<number | null>(null);
  
  // Location accuracy tracking (detects VPN/incognito)
  const [locationAccuracy, setLocationAccuracy] = useState<'high' | 'medium' | 'low' | 'unknown'>('unknown');
  
  // Quick location picker (saved locations)
  const [savedLocations, setSavedLocations] = useState<LocationProposal[]>([]);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  
  // Favorite locations from user profile
  const [favoriteLocations, setFavoriteLocations] = useState<Array<{
    id: string;
    name: string;
    address: string;
    coordinates: { lat: number; lng: number };
  }>>([]);
  
  // Location selection UI
  const [showLocationSelection, setShowLocationSelection] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [locationSearchResults, setLocationSearchResults] = useState<GeocodingResult[]>([]);
  const [locationSearchError, setLocationSearchError] = useState<string | null>(null);
  
  // Debounced search query (UX Best Practice - Performance)
  const debouncedSearchQuery = useDebounce(locationSearchQuery, 700);
  
  // Navigation
  const [navigationStatus, setNavigationStatus] = useState<{
    user1: NavigationStatus;
    user2: NavigationStatus;
  }>({
    user1: { started: false, arrived: false, gpsVerified: false, distanceFromLocation: 0 },
    user2: { started: false, arrived: false, gpsVerified: false, distanceFromLocation: 0 }
  });
  
  // On-the-way notification
  const [onTheWayStatus, setOnTheWayStatus] = useState<OnTheWayStatus>({
    user1OnTheWay: false,
    user2OnTheWay: false,
    user2Cancelled: false
  });
  const [showOnTheWayPopup, setShowOnTheWayPopup] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [showCancellationInput, setShowCancellationInput] = useState(false);

  // Maps app picker — shown instead of auto-opening a maps app so the user can
  // choose between Google Maps and Apple Maps (important on iPhone).
  const [mapsPicker, setMapsPicker] = useState<{ lat: number | null; lng: number | null; address: string } | null>(null);

  /** Open the platform-appropriate maps picker for a destination. */
  const openMapsPicker = (lat: number | null, lng: number | null, address: string) => {
    setMapsPicker({ lat, lng, address });
  };

  /** Build destination URLs for both map apps. */
  const buildMapsUrls = (lat: number | null, lng: number | null, address: string) => {
    const dest = (lat && lng) ? `${lat},${lng}` : encodeURIComponent(address);
    const destEncoded = encodeURIComponent(address || `${lat},${lng}`);
    return {
      google: `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`,
      apple: `https://maps.apple.com/?daddr=${dest}&dirflg=d&q=${destEncoded}`,
    };
  };
  
  // AI Relation Manager
  const [aiRelationManager, setAiRelationManager] = useState<AIRelationManagerData>({
    completed: false,
    skipped: false
  });
  const [showAIRelationManager, setShowAIRelationManager] = useState(false);
  
  // llmScript state removed — analysis is now stored per-user in connectionFlowAnalyses
  
  // Guided flow & expanded view (auto-open for physical meetings)
  const [showExpandedView, setShowExpandedView] = useState(true);
  const [guideDismissed, setGuideDismissed] = useState(false);
  
  // Refs
  // Stable Firestore ref — must not be recreated on every render
  const connectionRef = React.useMemo(
    () => doc(db, 'connections', connectionId),
    [connectionId]
  );
  // Throttle: only write userLocation to Firebase once per 30s to prevent snapshot loops
  const lastLocationWriteRef = useRef<number>(0);
  // Track last seen meetupStartedAt to avoid re-firing toast on every snapshot
  const lastMeetupStartedAtRef = useRef<string | null>(null);
  const [isUser1, setIsUser1] = useState<boolean | null>(null);
  

  // Determine if current user is user1
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const connectionDoc = await getDoc(connectionRef);
        if (connectionDoc.exists()) {
          const data = connectionDoc.data();
          setIsUser1(data.user1Id === currentUserId);
        }
      } catch (error) {
        console.error('Error checking user role:', error);
      }
    };
    checkUserRole();
  }, [connectionId, currentUserId]);
  
  // Load favorite locations from user profile
  useEffect(() => {
    const loadFavoriteLocations = async () => {
      if (!currentUserId) return;
      
      try {
        const profileDoc = await getDoc(doc(db, 'userProfiles', currentUserId));
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          if (data.favoriteLocations && Array.isArray(data.favoriteLocations)) {
            setFavoriteLocations(data.favoriteLocations);
          }
        }
      } catch (error) {
        console.error('Error loading favorite locations:', error);
      }
    };
    
    loadFavoriteLocations();
  }, [currentUserId]);
  
  // Toast when other user starts planning (transitions from false -> true)
  const prevOtherUserIsPlanning = useRef(false);
  useEffect(() => {
    if (otherUserIsPlanning.isPlanning && !prevOtherUserIsPlanning.current) {
      console.log('[PhysicalMeeting] Other user started planning — auto-opening agenda for User 2', otherUserIsPlanning);
      info(`${otherUserIsPlanning.userName} heeft "${otherUserIsPlanning.locationName}" gekozen en is nu de datum aan het plannen.`);
      // Keep user in the physical-location flow; do not auto-open agenda here.
      // Auto-opening can steal focus and cause overlay flicker during location selection.
    }
    prevOtherUserIsPlanning.current = otherUserIsPlanning.isPlanning;
  // onOpenAgenda excluded from deps — it's an unstable parent reference and this
  // effect must only fire when the planning state itself changes, not on every re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherUserIsPlanning.isPlanning, otherUserIsPlanning.userName, otherUserIsPlanning.locationName, info]);

  // Helper: Get user key (user1 or user2)
  const getUserKey = (): 'user1' | 'user2' => {
    return isUser1 ? 'user1' : 'user2';
  };

  // Live location hook — real-time via Socket.io + Firestore backup
  useLiveLocation({
    enabled: locationSharingEnabled && isUser1 !== null,
    connectionId,
    userId: currentUserId,
    userName: currentUserName,
    userKey: getUserKey(),
    partnerUserKey: isUser1 ? 'user2' : 'user1',
    onMyLocation: (loc) => {
      const userKey = getUserKey();
      setUserLocations(prev => ({ ...prev, [userKey]: loc }));
    },
    onPartnerLocation: (partnerKey, loc) => {
      setUserLocations(prev => ({
        ...prev,
        [partnerKey]: prev[partnerKey]
          ? { ...prev[partnerKey]!, lat: loc.lat, lng: loc.lng, lastUpdated: loc.lastUpdated }
          : loc,
      }));
    },
  });
  
  // Load initial data from Firebase
  useEffect(() => {
    // isFirstPhysicalSnapshot prevents toasts/popups based on already-existing Firestore data
    // from firing on every remount (page refresh, component re-mount). Only real-time deltas
    // that arrive after mount should trigger UI side-effects.
    let isFirstPhysicalSnapshot = true;

    const unsubscribe = onSnapshot(connectionRef, async (snapshot) => {
      if (!snapshot.exists()) return;
      
      const data = snapshot.data();
      const physicalData = data.physicalMeeting || {};
      
      // Update status — also sync from connection-level phaseStatus
      const connectionPhaseStatus = data.phaseStatus;
      if (physicalData.status) {
        setStatus(physicalData.status as PhysicalStatus);
      }
      
      // If connection-level phaseStatus is 'scheduled' but physicalMeeting.status is still 'planning',
      // that means the ExtendedAgenda just created an event — sync the physicalMeeting status
      if (connectionPhaseStatus === 'scheduled' && (!physicalData.status || physicalData.status === 'planning')) {
        setStatus('scheduled');
        // Also update Firebase so future loads are correct
        updateDoc(connectionRef, { 
          'physicalMeeting.status': 'scheduled',
          'physicalMeeting.selectedLocation.planningInProgress': false
        }).catch(err => console.error('Error syncing physical status:', err));
      }
      
      // Load location proposals - ALWAYS update, even if empty
      const locationProposals = physicalData.locationProposals || {};
      const user1Props = locationProposals.user1 || [];
      const user2Props = locationProposals.user2 || [];
      
      setUser1Proposals(user1Props.map((p: any) => ({
        ...p,
        proposedAt: p.proposedAt?.toDate() || new Date()
      })));
      setUser2Proposals(user2Props.map((p: any) => ({
        ...p,
        proposedAt: p.proposedAt?.toDate() || new Date()
      })));
      
      // Set current user proposals
      const isUser1 = currentUserId === data.user1Id;
      
      if (isUser1) {
        setCurrentUserProposals(user1Props.map((p: any) => ({
          ...p,
          proposedAt: p.proposedAt?.toDate() || new Date()
        })));
        setOtherUserProposals(user2Props.map((p: any) => ({
          ...p,
          proposedAt: p.proposedAt?.toDate() || new Date()
        })));
      } else {
        setCurrentUserProposals(user2Props.map((p: any) => ({
          ...p,
          proposedAt: p.proposedAt?.toDate() || new Date()
        })));
        setOtherUserProposals(user1Props.map((p: any) => ({
          ...p,
          proposedAt: p.proposedAt?.toDate() || new Date()
        })));
      }
      
      // Load location preferences (which locations users "liked")
      const prefs = physicalData.locationPreferences || {};
      const myKey = currentUserId === data.user1Id ? 'user1' : 'user2';
      const otherKey = myKey === 'user1' ? 'user2' : 'user1';
      setPreferredLocationIds(new Set(prefs[myKey] || []));
      setOtherPreferredIds(new Set(prefs[otherKey] || []));
      
      // Load best matches
      if (physicalData.bestMatches) {
        setBestMatches(physicalData.bestMatches);
      }
      
      // Load selected location — first try bestMatches lookup, then fall back to
      // inline location data stored directly on selectedLocation (for cases where
      // bestMatches was not loaded yet or was cleared after the fact)
      if (physicalData.selectedLocation) {
        const sel = physicalData.selectedLocation;
        const match = physicalData.bestMatches?.find(
          (m: BestMatch) => m.locationId === sel.locationId
        );
        if (match) {
          setSelectedLocation(match);
        } else if (sel.location) {
          // Reconstruct a minimal BestMatch from the stored selectedLocation data
          setSelectedLocation({
            locationId: sel.locationId || 'saved',
            location: sel.location,
            user1Distance: 0,
            user2Distance: 0,
            user1TravelTime: 0,
            user2TravelTime: 0,
            totalDistance: 0,
            matchQuality: 'best',
          });
        }
        
        // Check if OTHER user is currently planning (selected location but not yet scheduled)
        if (sel.planningInProgress && sel.selectedBy !== currentUserId) {
          setOtherUserIsPlanning({
            isPlanning: true,
            userName: sel.selectedByName || otherUserName,
            locationName: sel.location?.name || sel.location?.address || 'een locatie'
          });
        } else {
          setOtherUserIsPlanning({ isPlanning: false, userName: '', locationName: '' });
        }
      } else {
        setOtherUserIsPlanning({ isPlanning: false, userName: '', locationName: '' });
      }
      
      // Load confirmed appointment datetime (for 24h navigation lock)
      const apptStart = data.appointmentStart || physicalData.scheduledAt || physicalData.appointmentStart;
      if (apptStart) {
        const apptDate = apptStart?.toDate ? apptStart.toDate() : new Date(apptStart);
        setAppointmentDateTime(apptDate);
      }

      // Load user locations
      if (physicalData.userLocations) {
        setUserLocations({
          user1: physicalData.userLocations.user1 ? {
            ...physicalData.userLocations.user1,
            lastUpdated: physicalData.userLocations.user1.lastUpdated?.toDate() || new Date()
          } : null,
          user2: physicalData.userLocations.user2 ? {
            ...physicalData.userLocations.user2,
            lastUpdated: physicalData.userLocations.user2.lastUpdated?.toDate() || new Date()
          } : null
        });
      }
      
      // Load navigation status
      if (physicalData.navigationStatus) {
        setNavigationStatus(physicalData.navigationStatus);
      }
      
      // Load on-the-way status
      if (physicalData.onTheWayStatus) {
        const onTheWayData = physicalData.onTheWayStatus;
        setOnTheWayStatus({
          ...onTheWayData,
          user1OnTheWayAt: onTheWayData.user1OnTheWayAt?.toDate(),
          user2OnTheWayAt: onTheWayData.user2OnTheWayAt?.toDate()
        });
        
        // Determine user role from snapshot data (avoids extra getDoc inside onSnapshot)
        const isCurrentUser1 = currentUserId === data.user1Id;
        const isCurrentUser2 = currentUserId === data.user2Id;

        // Only show the popup for live events — not for data that was already
        // in Firestore when the component mounted (stale-toast guard).

        // User 2 sees popup when User 1 goes on the way
        if (
          !isFirstPhysicalSnapshot &&
          isCurrentUser2 &&
          onTheWayData.user1OnTheWay &&
          !onTheWayData.user2OnTheWay &&
          !onTheWayData.user2Cancelled
        ) {
          setShowOnTheWayPopup(true);
        }

        // User 1 sees popup when User 2 goes on the way (symmetric)
        if (
          !isFirstPhysicalSnapshot &&
          isCurrentUser1 &&
          onTheWayData.user2OnTheWay &&
          !onTheWayData.user1OnTheWay &&
          !onTheWayData.user1Cancelled
        ) {
          setShowOnTheWayPopup(true);
        }
      }

      // Notify user 2 when the other user pressed "Start".
      // Guard 1: meetupStartedAt field must exist and belong to the other user.
      // Guard 2: meeting must still be active — prevents stale toast after page refresh
      //          when the field was not yet deleted (e.g. edge case: endMeeting failed).
      const meetingIsActive = physicalData.status === 'navigation' || physicalData.status === 'active';
      if (
        !isFirstPhysicalSnapshot &&
        meetingIsActive &&
        physicalData.meetupStartedAt &&
        physicalData.meetupStartedBy &&
        physicalData.meetupStartedBy !== currentUserId
      ) {
        const startedAtKey = physicalData.meetupStartedAt?.seconds?.toString() ?? physicalData.meetupStartedAt?.toString();
        if (startedAtKey && lastMeetupStartedAtRef.current !== startedAtKey) {
          lastMeetupStartedAtRef.current = startedAtKey;
          success(`▶ ${otherUserName} heeft de meeting gestart!`);
        }
      }
      
      // AI Relation Manager: per-user independent flow.
      // Show reflection form as soon as THIS user's meeting ends (status = 'completed'),
      // regardless of whether the other user has also ended.
      const userKeyForAI = currentUserId === data.user1Id ? 'user1' : 'user2';
      const userAiData = physicalData.aiRelationManager?.[userKeyForAI];

      if (userAiData) {
        setAiRelationManager(userAiData);
      }

      if (userAiData?.completed || userAiData?.skipped) {
        // This user already finished their reflection
        setShowAIRelationManager(false);
      } else if (physicalData.status === 'completed') {
        // Meeting ended — show reflection form immediately to this user
        setShowAIRelationManager(true);
      } else {
        setShowAIRelationManager(false);
      }

      // All state is now loaded from Firestore. Subsequent snapshots are live events.
      isFirstPhysicalSnapshot = false;
    });
    
    return () => unsubscribe();
  }, [connectionId, currentUserId, otherUserId]);
  
  // Load saved locations from user profile
  useEffect(() => {
    const loadSavedLocations = async () => {
      try {
        const userProfileRef = doc(db, 'userProfiles', currentUserId);
        const profileDoc = await getDoc(userProfileRef);
        
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          const saved = profileData.locationPreferences?.savedProposals || [];
          setSavedLocations(saved.map((p: any) => ({
            ...p,
            proposedAt: p.createdAt?.toDate() || new Date()
          })));
        }
      } catch (error) {
        console.error('Error loading saved locations:', error);
      }
    };
    
    loadSavedLocations();
  }, [currentUserId]);

  // Auto-detect current user's browser location (works on desktop via IP/WiFi, like Google Maps)
  useEffect(() => {
    if (isUser1 === null) return;
    const myKey = isUser1 ? 'user1' : 'user2';
    
    // Skip if already have location for current user
    if (userLocations[myKey]) return;
    
    if (!navigator.geolocation) {
      console.log('[PhysicalMeeting] Geolocation API not available');
      return;
    }
    
    console.log('[PhysicalMeeting] Requesting browser geolocation (one-time)...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracyMeters = position.coords.accuracy;
        console.log(`[PhysicalMeeting] Browser geolocation success:`, { lat, lng, accuracy: accuracyMeters });
        
        // Determine accuracy level
        // < 100m = high (real GPS), < 5km = medium (WiFi), < 50km = low (IP/cell), > 50km = unknown (VPN?)
        const accLevel: 'high' | 'medium' | 'low' | 'unknown' = 
          accuracyMeters < 100 ? 'high' :
          accuracyMeters < 5000 ? 'medium' :
          accuracyMeters < 50000 ? 'low' : 'unknown';
        
        setLocationAccuracy(accLevel);
        console.log(`[PhysicalMeeting] Location accuracy: ${accLevel} (${accuracyMeters}m)`);
        
        const address = await reverseGeocode(lat, lng) || 'Mijn locatie';
        
        setUserLocations(prev => ({
          ...prev,
          [myKey]: prev[myKey] || {
            lat, lng, address,
            lastUpdated: new Date(),
            sharingEnabled: false
          }
        }));
        
        // Save to Firebase — throttled to max once per 30s to prevent snapshot cascade
        const now = Date.now();
        if (now - lastLocationWriteRef.current >= 30000) {
          lastLocationWriteRef.current = now;
          await updateDoc(connectionRef, {
            [`physicalMeeting.userLocations.${myKey}`]: {
              lat, lng, address,
              accuracy: accLevel,
              lastUpdated: serverTimestamp(),
              sharingEnabled: false
            }
          }).catch(err => console.error('[PhysicalMeeting] Error saving browser location:', err));
        }
      },
      (error) => {
        console.log('[PhysicalMeeting] Browser geolocation denied/failed:', error.message);
        setLocationAccuracy('unknown');
        // Not critical - will fall back to profile address or proposals centroid
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, [isUser1, connectionId]);

  // Auto-load user locations from profile addresses (office/company/location)
  // This runs as fallback if browser geolocation is denied or unavailable
  useEffect(() => {
    if (isUser1 === null) return;
    
    // Small delay to let browser geolocation resolve first
    const timer = setTimeout(async () => {
      const resolveFromProfile = async (userId: string, userKey: 'user1' | 'user2') => {
        // Skip if already resolved (from browser geolocation or Firebase)
        if (userLocations[userKey]) return null;
        
        console.log(`[PhysicalMeeting] Resolving profile location for ${userKey} (${userId})...`);
        
        const loc = await getUserOfficeLocation(userId);
        console.log(`[PhysicalMeeting] getUserOfficeLocation result for ${userKey}:`, loc);
        
        if (!loc) return null;
        
        if (loc.coordinates) {
          return { lat: loc.coordinates.lat, lng: loc.coordinates.lng, address: loc.address || loc.name || 'Profiel locatie', lastUpdated: new Date(), sharingEnabled: false };
        }
        
        if (loc.address) {
          console.log(`[PhysicalMeeting] Geocoding profile address for ${userKey}:`, loc.address);
          const result = await geocodeAddress(loc.address);
          if (result) {
            return { lat: result.lat, lng: result.lng, address: loc.address, lastUpdated: new Date(), sharingEnabled: false };
          }
        }
        
        return null;
      };
      
      try {
        const connectionDoc = await getDoc(connectionRef);
        if (!connectionDoc.exists()) return;
        const connData = connectionDoc.data();

        const resolved1 = await resolveFromProfile(connData.user1Id, 'user1');
        if (resolved1) setUserLocations(prev => ({ ...prev, user1: prev.user1 || resolved1 }));

        const resolved2 = await resolveFromProfile(connData.user2Id, 'user2');
        if (resolved2) setUserLocations(prev => ({ ...prev, user2: prev.user2 || resolved2 }));
      } catch (error) {
        console.error('[PhysicalMeeting] Error loading profile locations:', error);
      }
    }, 3000); // 3s delay to let browser geolocation try first
    
    return () => clearTimeout(timer);
  }, [isUser1, connectionId]);
  
  // Real-time location sharing is handled by useLiveLocation hook above
  
  // MANUAL best match calculation (triggered by user button click)
  const handleCalculateBestMatches = async () => {
    if (user1Proposals.length < 3 || user2Proposals.length < 3) {
      showError('Beide gebruikers moeten minimaal 3 locaties voorstellen');
      return;
    }
    
    setLoading('calculatingMatches', true);
    try {
      // Get user locations (or use office locations as fallback)
      const user1Loc = userLocations.user1 || await getUserLocationForUser('user1');
      const user2Loc = userLocations.user2 || await getUserLocationForUser('user2');
      
      if (!user1Loc || !user2Loc) {
        showError('Kon locaties niet bepalen. Voeg minimaal 3 locatievoorstellen toe per gebruiker.');
        return;
      }
      
      info('Berekenen van beste matches...');
      
      // Combine all proposals
      const allProposals = [
        ...user1Proposals.map(p => ({ ...p, id: `user1-${p.id}` })),
        ...user2Proposals.map(p => ({ ...p, id: `user2-${p.id}` }))
      ];
      
      // Calculate best matches
      const matches = await calculateBestMatches(
        allProposals,
        { lat: user1Loc.lat, lng: user1Loc.lng },
        { lat: user2Loc.lat, lng: user2Loc.lng }
      );
      
      setBestMatches(matches);
      
      // Save to Firebase
      await updateDoc(connectionRef, {
        'physicalMeeting.bestMatches': matches
      });
      
      success(`${matches.length} beste matches gevonden!`);
    } catch (error) {
      console.error('Error calculating best matches:', error);
      showError('Fout bij berekenen van matches. Probeer het opnieuw.');
    } finally {
      setLoading('calculatingMatches', false);
    }
  };
  
  // Helper: Get user location (from GPS, office, or proposals centroid)
  const getUserLocationForUser = async (userKey: 'user1' | 'user2'): Promise<{ lat: number; lng: number } | null> => {
    try {
      // 1. Try GPS location first
      const gpsLoc = userLocations[userKey];
      if (gpsLoc) {
        return { lat: gpsLoc.lat, lng: gpsLoc.lng };
      }
      
      // 2. Fallback to office/profile location
      const connectionDoc = await getDoc(connectionRef);
      if (connectionDoc.exists()) {
        const data = connectionDoc.data();
        const userId = userKey === 'user1' ? data.user1Id : data.user2Id;
        
        const officeLoc = await getUserOfficeLocation(userId);
        if (officeLoc?.coordinates) {
          return { lat: officeLoc.coordinates.lat, lng: officeLoc.coordinates.lng };
        }
        
        // Try geocoding office address
        if (officeLoc?.address) {
          const geocoded = await geocodeAddress(officeLoc.address);
          if (geocoded) {
            return { lat: geocoded.lat, lng: geocoded.lng };
          }
        }
      }
      
      // 3. Fallback to centroid of user's own proposals (always works on desktop)
      const proposals = userKey === 'user1' ? user1Proposals : user2Proposals;
      if (proposals.length > 0) {
        const avgLat = proposals.reduce((sum, p) => sum + p.lat, 0) / proposals.length;
        const avgLng = proposals.reduce((sum, p) => sum + p.lng, 0) / proposals.length;
        return { lat: avgLat, lng: avgLng };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user location:', error);
      return null;
    }
  };
  
  // Toggle preference for a location (like/unlike)
  const toggleLocationPreference = async (locationId: string) => {
    try {
      const userKey = getUserKey();
      const newPrefs = new Set(preferredLocationIds);
      
      if (newPrefs.has(locationId)) {
        newPrefs.delete(locationId);
      } else {
        newPrefs.add(locationId);
      }
      
      setPreferredLocationIds(newPrefs);
      
      // Save to Firebase
      await updateDoc(connectionRef, {
        [`physicalMeeting.locationPreferences.${userKey}`]: Array.from(newPrefs)
      });
    } catch (error) {
      console.error('Error toggling location preference:', error);
    }
  };

  // Add location proposal (with error handling and feedback)
  const addLocationProposal = async (location: GeocodingResult, name?: string) => {
    console.log('🏁 addLocationProposal START', { location, name });
    setLoading('addingProposal', true);
    setLocationSearchError(null);
    
    try {
      const proposal: LocationProposal = {
        id: Date.now().toString(),
        name: name || location.address,
        address: location.address,
        lat: location.lat,
        lng: location.lng,
        proposedAt: new Date()
      };
      console.log('📦 Created proposal:', proposal);
      
      // Determine which user is adding
      const userKey = getUserKey();
      
      // Check if user already has 3 proposals (can add more, but warn)
      const currentProposals = userKey === 'user1' ? user1Proposals : user2Proposals;
      console.log(`📊 Current proposals for ${userKey}:`, currentProposals.length, currentProposals);
      
      if (currentProposals.length >= 3) {
        // User already has minimum, but can add more
        info('Je hebt al 3 voorstellen. Je kunt er meer toevoegen als je wilt.');
      }
      
      // Add to local state (optimistic update)
      if (userKey === 'user1') {
        setUser1Proposals(prev => {
          console.log('📝 Updating user1Proposals from', prev.length, 'to', prev.length + 1);
          return [...prev, proposal];
        });
      } else {
        setUser2Proposals(prev => {
          console.log('📝 Updating user2Proposals from', prev.length, 'to', prev.length + 1);
          return [...prev, proposal];
        });
      }
      
      // Update Firebase
      const newProposals = [...currentProposals, proposal];
      console.log('🔥 Updating Firebase with proposals:', newProposals);
      await updateDoc(connectionRef, {
        [`physicalMeeting.locationProposals.${userKey}`]: newProposals
      });
      console.log('✅ Firebase updated successfully');
      
      setLocationSearchQuery('');
      setLocationSearchResults([]);
      success(`Locatie "${proposal.name}" toegevoegd`);
    } catch (err: any) {
      console.error('❌ Error adding location proposal:', err);
      const errorMessage = err.message || 'Fout bij toevoegen van locatie. Probeer het opnieuw.';
      showError(errorMessage);
      setLocationSearchError(errorMessage);
      
      // Rollback optimistic update
      const userKey = getUserKey();
      if (userKey === 'user1') {
        setUser1Proposals(prev => prev.slice(0, -1));
      } else {
        setUser2Proposals(prev => prev.slice(0, -1));
      }
    } finally {
      setLoading('addingProposal', false);
    }
  };
  
  // Search location (with debouncing via useEffect)
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery || debouncedSearchQuery.length < 2) {
        setLocationSearchResults([]);
        setLocationSearchError(null);
        return;
      }
      
      setLoading('searchingLocation', true);
      setLocationSearchError(null);
      
      try {
        const result = await geocodeAddress(debouncedSearchQuery);
        if (result) {
          setLocationSearchResults([result]);
          setLocationSearchError(null);
        } else {
          setLocationSearchResults([]);
          setLocationSearchError('Geen locatie gevonden. Probeer een andere zoekterm.');
        }
      } catch (err: any) {
        console.error('Error searching location:', err);
        setLocationSearchResults([]);
        const errorMessage = err.message || 'Fout bij zoeken. Probeer het opnieuw.';
        setLocationSearchError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading('searchingLocation', false);
      }
    };
    
    performSearch();
  }, [debouncedSearchQuery]);
  
  // Select location from best matches (with feedback)
  // First user to select gets priority — checks Firebase for existing selection
  // After selecting location, opens ExtendedAgenda for date/time planning
  const selectLocation = async (match: BestMatch) => {
    console.log('[selectLocation] Called with match:', { locationId: match.locationId, locationName: match.location.name || match.location.address, currentUserId, otherUserId, connectionId });
    try {
      // Check if someone already selected a location (first-come-first-served)
      const connectionDoc = await getDoc(connectionRef);
      const data = connectionDoc.data();
      const existingSelection = data?.physicalMeeting?.selectedLocation;
      
      if (existingSelection && existingSelection.selectedBy !== currentUserId && existingSelection.scheduledAt) {
        // Other user already fully scheduled — accept their choice
        const existingMatch = bestMatches.find(m => m.locationId === existingSelection.locationId);
        if (existingMatch) {
          setSelectedLocation(existingMatch);
          setStatus('scheduled');
          success(`${otherUserName} heeft al een locatie gekozen en ingepland: "${existingMatch.location.name || existingMatch.location.address}"`);
          return;
        }
      }
      
      setSelectedLocation(match);
      
      // Save selected location to Firebase + mark that this user is planning
      console.log('[selectLocation] Writing to connection doc:', connectionId);
      await updateDoc(connectionRef, {
        'physicalMeeting.selectedLocation': {
          locationId: match.locationId,
          selectedBy: currentUserId,
          selectedByName: currentUserName,
          selectedAt: serverTimestamp(),
          acceptedBy: [currentUserId],
          location: match.location,
          planningInProgress: true
        }
      });
      console.log('[selectLocation] Connection doc updated successfully');

      // Stuur directe planningNotification naar User 2
      console.log('[selectLocation] Writing planningNotification for otherUserId:', otherUserId);
      try {
        const notifRef = await addDoc(collection(db, 'planningNotifications'), {
          toUserId: otherUserId,
          fromUserId: currentUserId,
          fromUserName: currentUserName,
          connectionId: connectionId,
          type: 'location_selected_planning',
          locationName: match.location.name || match.location.address,
          message: `${currentUserName} heeft "${match.location.name || match.location.address}" gekozen en plant nu de datum`,
          read: false,
          createdAt: serverTimestamp()
        });
        console.log('✅ planningNotification gestuurd naar User 2, docId:', notifRef.id);
      } catch (notifErr) {
        console.warn('⚠️ Kon planningNotification niet schrijven:', notifErr);
      }

      success(`Locatie "${match.location.name || match.location.address}" gekozen! Plan nu een datum en tijd.`);
      
      // Open the ExtendedAgenda for scheduling
      if (onOpenAgenda) {
        onOpenAgenda();
      }
    } catch (err: any) {
      console.error('Error selecting location:', err);
      const errorMessage = err.message || 'Fout bij selecteren van locatie. Probeer het opnieuw.';
      showError(errorMessage);
    }
  };
  
  /**
   * Resolve the meeting location from local state OR Firestore as fallback.
   *
   * When the appointment was scheduled via ExtendedAgenda (not via the
   * PhysicalMeetingPhaseV2 location picker), physicalMeeting.selectedLocation
   * may never have been written to Firestore, so the local selectedLocation
   * state can be null even when status is 'navigation'. This helper reads
   * directly from Firestore and also updates the local state so subsequent
   * calls don't need another round-trip.
   *
   * Returns the resolved BestMatch, or null if no location data is available.
   */
  const resolveLocation = async (): Promise<typeof selectedLocation> => {
    if (selectedLocation) return selectedLocation;

    try {
      const snap = await getDoc(connectionRef);
      const data = snap.data();
      const sel = data?.physicalMeeting?.selectedLocation;

      if (sel?.location) {
        const rawLoc = sel.location as { lat: number | null; lng: number | null; address: string; name?: string };

        // If lat/lng are null (stub written by appointmentService), geocode now
        if (rawLoc.lat == null || rawLoc.lng == null) {
          const addressToGeocode = rawLoc.address || rawLoc.name || '';
          if (addressToGeocode) {
            try {
              const geo = await geocodeAddress(addressToGeocode);
              if (geo?.lat != null && geo?.lng != null) {
                rawLoc.lat = geo.lat;
                rawLoc.lng = geo.lng;
                // Persist resolved coords back so next load skips geocoding
                updateDoc(connectionRef, {
                  'physicalMeeting.selectedLocation.location': rawLoc,
                }).catch(() => {});
              }
            } catch {
              // geocode failure — proceed with null coords, GPS check will be skipped
            }
          }
        }

        if (rawLoc.lat != null && rawLoc.lng != null) {
          const restored: BestMatch = {
            locationId: sel.locationId || 'saved',
            location: rawLoc as { lat: number; lng: number; address: string; name?: string },
            user1Distance: 0,
            user2Distance: 0,
            user1TravelTime: 0,
            user2TravelTime: 0,
            totalDistance: 0,
            matchQuality: 'best',
          };
          setSelectedLocation(restored);
          return restored;
        }
      }

      // Last resort: try to read from the linked calendarEvent
      const eventId = data?.activeEvent?.eventId;
      if (eventId) {
        const eventSnap = await getDoc(doc(db, 'calendarEvents', eventId));
        const ev = eventSnap.data();
        if (ev) {
          const locationName = ev.location || '';
          const locationAddress = ev.locationAddress || locationName;
          // Attempt geocoding to get coordinates
          if (locationAddress) {
            try {
              const geo = await geocodeAddress(locationAddress);
              if (geo?.lat != null && geo?.lng != null) {
                const restoredLocation = { lat: geo.lat, lng: geo.lng, address: locationAddress, name: locationName };
                const restored: BestMatch = {
                  locationId: eventId,
                  location: restoredLocation,
                  user1Distance: 0,
                  user2Distance: 0,
                  user1TravelTime: 0,
                  user2TravelTime: 0,
                  totalDistance: 0,
                  matchQuality: 'best',
                };
                // Persist to Firestore so future loads work without geocoding again
                updateDoc(connectionRef, {
                  'physicalMeeting.selectedLocation': {
                    locationId: eventId,
                    location: restoredLocation,
                    selectedBy: 'system',
                    selectedAt: serverTimestamp(),
                  },
                }).catch(() => {});
                setSelectedLocation(restored);
                return restored;
              }
            } catch {
              // geocode failure — fall through to null
            }
          }
        }
      }
    } catch (err) {
      console.warn('[PhysicalMeeting] resolveLocation fallback failed:', err);
    }

    return null;
  };

  // Start navigation (with GPS permission check)
  const startNavigation = async () => {
    // Check GPS permission first
    if (permissionStatus === 'denied' || !gpsSupported) {
      const granted = await requestPermission();
      if (!granted) {
        showError('GPS toegang is nodig voor navigatie. Activeer GPS in je browser instellingen.');
        return;
      }
    }
    
    setLoading('startingNavigation', true);
    try {
      const userKey = getUserKey();
      
      // Update navigation status
      await updateDoc(connectionRef, {
        [`physicalMeeting.navigationStatus.${userKey}`]: {
          started: true,
          startedAt: serverTimestamp(),
          arrived: false,
          gpsVerified: false,
          distanceFromLocation: 0
        },
        'physicalMeeting.status': 'navigation'
      });
      
      // ✅ Update presence: on the way
      await updatePresence(connectionId, currentUserId, currentUserName, 'on_the_way');
      
      setStatus('navigation');

      // Auto-start real-time GPS tracking so the in-app map shows live position
      setLocationSharingEnabled(true);

      success('Navigatie gestart — kaart volgt je positie');

      // Mark this user as "on the way" — symmetric for both user1 and user2.
      // This triggers the popup for the OTHER user via the onSnapshot listener.
      await updateDoc(connectionRef, {
        [`physicalMeeting.onTheWayStatus.${userKey}OnTheWay`]: true,
        [`physicalMeeting.onTheWayStatus.${userKey}OnTheWayAt`]: serverTimestamp(),
      });
    } catch (err: any) {
      console.error('Error starting navigation:', err);
      const errorMessage = err.message || 'Fout bij starten van navigatie. Probeer het opnieuw.';
      showError(errorMessage);
    } finally {
      setLoading('startingNavigation', false);
    }
  };
  
  // Verify GPS location (< 10km from meeting location)
  const verifyGPSLocation = async () => {
    // Try to resolve location from Firestore if local state is missing.
    // This handles the case where the appointment was scheduled via ExtendedAgenda
    // and physicalMeeting.selectedLocation was never written.
    const location = await resolveLocation();

    if (!location) {
      // No location data available at all — allow arriving without GPS check
      const userKey = getUserKey();
      await updateDoc(connectionRef, {
        [`physicalMeeting.navigationStatus.${userKey}`]: {
          started: true,
          arrived: true,
          arrivedAt: serverTimestamp(),
          gpsVerified: false,
          distanceFromLocation: 0,
        },
      }).catch(() => {});
      setLocationSharingEnabled(false);
      locationSocket.disconnect();
      success('Aangekomen! (GPS verificatie overgeslagen — geen locatie beschikbaar)');
      return;
    }

    if (!navigator.geolocation) {
      showError('GPS is niet beschikbaar op dit apparaat');
      return;
    }
    
    setLoading('verifyingGPS', true);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          // Calculate distance
          const { calculateDistance } = await import('../../services/geocodingService');
          const distance = calculateDistance(
            userLat,
            userLng,
            location.location.lat,
            location.location.lng
          );
          
          const userKey = getUserKey();
          
          if (distance <= 0.1) {
            // User is at location
            await updateDoc(connectionRef, {
              [`physicalMeeting.navigationStatus.${userKey}`]: {
                started: true,
                arrived: true,
                arrivedAt: serverTimestamp(),
                gpsVerified: true,
                distanceFromLocation: distance
              }
            });
            setLocationSharingEnabled(false);
            locationSocket.disconnect();
            success(`Je bent op locatie! 📍`);
          } else {
            // User is not at location
            await updateDoc(connectionRef, {
              [`physicalMeeting.navigationStatus.${userKey}`]: {
                started: true,
                arrived: false,
                gpsVerified: false,
                distanceFromLocation: distance
              }
            });
            
            warning(`Je bent nog ${distance < 1 ? Math.round(distance * 1000) + 'm' : distance.toFixed(1) + 'km'} van de locatie. Kom dichterbij en probeer opnieuw.`);
          }
        } catch (err: any) {
          console.error('Error verifying GPS:', err);
          showError('Fout bij verificeren van locatie. Probeer het opnieuw.');
        } finally {
          setLoading('verifyingGPS', false);
        }
      },
      (error) => {
        console.error('GPS error:', error);
        let errorMessage = 'GPS fout: ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'GPS toegang is geweigerd';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Locatie informatie is niet beschikbaar';
            break;
          case error.TIMEOUT:
            errorMessage += 'Het ophalen van je locatie duurde te lang';
            break;
          default:
            errorMessage += error.message || 'Onbekende fout';
        }
        showError(errorMessage);
        setLoading('verifyingGPS', false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };
  
  // Start meeting
  const startMeeting = async () => {
    // Resolve location from Firestore if local state is null (defensive guard —
    // startMeeting should only be reachable when arrived, so location is usually set)
    const location = await resolveLocation();
    if (!location) {
      showError('Geen locatie geselecteerd');
      return;
    }
    
    setLoading('startingMeeting', true);
    
    try {
      // Check if both users are at location
      const connectionDoc = await getDoc(connectionRef);
      const data = connectionDoc.data();
      const navStatus = data?.physicalMeeting?.navigationStatus;
      
      if (!navStatus?.user1?.arrived || !navStatus?.user2?.arrived) {
        warning('Beide gebruikers moeten op locatie zijn om de meeting te starten');
        setLoading('startingMeeting', false);
        return;
      }
      
      await updateDoc(connectionRef, {
        'physicalMeeting.status': 'active',
        'physicalMeeting.meetingStartTime': serverTimestamp()
      });
      
      // ✅ Update presence: in meeting
      await updatePresence(connectionId, currentUserId, currentUserName, 'in_meeting');
      
      setStatus('active');
      success('Meeting gestart!');
    } catch (err: any) {
      console.error('Error starting meeting:', err);
      const errorMessage = err.message || 'Fout bij starten van meeting. Probeer het opnieuw.';
      showError(errorMessage);
    } finally {
      setLoading('startingMeeting', false);
    }
  };
  
  // End meeting
  const endMeeting = async () => {
    setLoading('endingMeeting', true);
    try {
      await updateDoc(connectionRef, {
        'physicalMeeting.status': 'completed',
        'physicalMeeting.meetingEndTime': serverTimestamp(),
        // Clean up start-notification fields so the "meeting started" toast
        // cannot re-fire after a page refresh when the meeting is already over.
        'physicalMeeting.meetupStartedAt': deleteField(),
        'physicalMeeting.meetupStartedBy': deleteField(),
      });
      
      // ✅ Update presence: back to idle
      await updatePresence(connectionId, currentUserId, currentUserName, 'idle');
      
      setStatus('completed');
      setShowAIRelationManager(true);
      success('Meeting beëindigd');
    } catch (err: any) {
      console.error('Error ending meeting:', err);
      const errorMessage = err.message || 'Fout bij beëindigen van meeting. Probeer het opnieuw.';
      showError(errorMessage);
    } finally {
      setLoading('endingMeeting', false);
    }
  };
  
  // Meeting duration tracking
  const meetingStartTimeRef = useRef<number | null>(null);
  const [meetingDurationMinutes, setMeetingDurationMinutes] = useState<number | null>(null);
  const [customExpertTag, setCustomExpertTag] = useState('');

  // Spar flow questions state — extended with structured GEO fields
  const [sparAnswers, setSparAnswers] = useState({
    feeling: '',
    highlights: '',
    followUp: '',
    experience: '',
    outcomes: '',
    expertiseTags: [] as string[],
    expertTagline: '',
    meetingTopic: '',
    wouldRecommend: '' as 'yes' | 'no' | '',
    recommendationText: '',
  });
  const [sparErrors, setSparErrors] = useState<Record<string, string[]>>({});
  const [showSparQuestions, setShowSparQuestions] = useState(false);
  
  // Form validation rules for Spar
  const sparValidationRules: Record<string, ValidationRule> = {
    feeling: { required: true, minLength: 10, maxLength: 500 },
    highlights: { required: true, minLength: 10, maxLength: 500 },
    followUp: { required: false, maxLength: 500 },
    experience: { required: true, minLength: 10, maxLength: 500 },
    outcomes: { required: false, maxLength: 500 },
    expertTagline: { required: true, minLength: 10, maxLength: 150 },
  };
  
  // Handle AI Relation Manager
  const handleAIRelationManager = async (action: 'spar' | 'skip') => {
    const userKey = getUserKey();
    if (action === 'skip') {
      await updateDoc(connectionRef, {
        [`physicalMeeting.aiRelationManager.${userKey}`]: {
          skipped: true,
          completed: false,
          answers: null,
          completedAt: serverTimestamp()
        }
      });
      
      setAiRelationManager({ completed: false, skipped: true });
      setShowAIRelationManager(false);
      
      // Finalize per-user (no answers on skip), then check if both done
      await finalizeUserReflection(null);
    } else {
      // Show spar questions
      setShowSparQuestions(true);
    }
  };
  
  // Submit spar answers (with validation)
  const submitSparAnswers = async () => {
    // Validate form
    const errors: Record<string, string[]> = {};
    Object.keys(sparValidationRules).forEach((key) => {
      const validation = validateField(
        sparAnswers[key as keyof typeof sparAnswers],
        sparValidationRules[key],
        key
      );
      if (!validation.isValid) {
        errors[key] = validation.errors;
      }
    });
    
    if (Object.keys(errors).length > 0) {
      setSparErrors(errors);
      showError('Controleer de verplichte velden en probeer het opnieuw');
      return;
    }
    
    const userKey = getUserKey();
    setLoading('submittingSpar', true);
    
    try {
      await updateDoc(connectionRef, {
        [`physicalMeeting.aiRelationManager.${userKey}`]: {
          completed: true,
          skipped: false,
          answers: sparAnswers,
          completedAt: serverTimestamp()
        }
      });
      
      setAiRelationManager({ completed: true, skipped: false, answers: sparAnswers });
      setShowSparQuestions(false);
      setShowAIRelationManager(false);
      success('Je antwoorden zijn opgeslagen');
      
      // Finalize per-user analysis + AI enrichment + status transition
      await finalizeUserReflection(sparAnswers);
    } catch (err: any) {
      console.error('Error submitting spar answers:', err);
      const errorMessage = err.message || 'Fout bij opslaan van antwoorden. Probeer het opnieuw.';
      showError(errorMessage);
    } finally {
      setLoading('submittingSpar', false);
    }
  };
  
  // Handle on-the-way notification responses — works symmetrically for both user1 and user2
  const handleOnTheWayResponse = async (response: 'yes' | 'no' | 'cancel') => {
    if (response === 'cancel') {
      setShowCancellationInput(true);
      return;
    }

    const userKey = getUserKey(); // 'user1' or 'user2'

    try {
      if (response === 'yes') {
        await updateDoc(connectionRef, {
          [`physicalMeeting.onTheWayStatus.${userKey}OnTheWay`]: true,
          [`physicalMeeting.onTheWayStatus.${userKey}OnTheWayAt`]: serverTimestamp(),
          [`physicalMeeting.onTheWayStatus.${userKey}Cancelled`]: false,
        });
        success('Je hebt bevestigd dat je onderweg bent');

        // Show maps app picker so the user can choose Google Maps or Apple Maps.
        const loc = await resolveLocation();
        if (loc) {
          const { lat, lng, address, name } = loc.location;
          openMapsPicker(lat ?? null, lng ?? null, name || address || '');
        }
      } else {
        await updateDoc(connectionRef, {
          [`physicalMeeting.onTheWayStatus.${userKey}OnTheWay`]: false,
          [`physicalMeeting.onTheWayStatus.${userKey}Cancelled`]: false,
        });
        info('Je hebt aangegeven dat je nog niet onderweg bent');
      }

      setShowOnTheWayPopup(false);
    } catch (err: any) {
      console.error('Error handling on-the-way response:', err);
      const errorMessage = err.message || 'Fout bij verwerken van reactie. Probeer het opnieuw.';
      showError(errorMessage);
    }
  };
  
  // Submit cancellation with reason — symmetric for both user1 and user2
  const submitCancellation = async () => {
    if (!cancellationReason.trim()) {
      showError('Geef een reden op voor het afzeggen');
      return;
    }

    const userKey = getUserKey(); // 'user1' or 'user2'

    try {
      await updateDoc(connectionRef, {
        [`physicalMeeting.onTheWayStatus.${userKey}Cancelled`]: true,
        'physicalMeeting.onTheWayStatus.cancellationReason': cancellationReason,
        [`physicalMeeting.onTheWayStatus.${userKey}OnTheWay`]: false,
      });
      
      // Note: LLM wordt NIET aangevuld als user 2 afzegt
      setShowOnTheWayPopup(false);
      setShowCancellationInput(false);
      setCancellationReason('');
      warning('Meeting afgezegd. LLM flow wordt niet geactiveerd.');
    } catch (err: any) {
      console.error('Error submitting cancellation:', err);
      const errorMessage = err.message || 'Fout bij afzeggen. Probeer het opnieuw.';
      showError(errorMessage);
    }
  };
  
  // Trigger background AI analysis via Cloud Function (non-blocking, best-effort)
  // The Cloud Function uses the securely stored OpenAI key — the client key is NOT used.
  const triggerAIAnalysis = async (answers: typeof sparAnswers) => {
    try {
      const analyzeConversation = httpsCallable<
        { connectionId: string; phase: string; userId: string; sparAnswers: Record<string, any> },
        { success: boolean; analysis: any; otherUserName: string }
      >(functions, 'analyzeConversation');

      // Serialize array fields to string for Cloud Function compatibility
      const serializedAnswers: Record<string, any> = {
        ...answers,
        expertiseTags: answers.expertiseTags.join(', '),
      };

      const result = await analyzeConversation({
        connectionId,
        phase: 'physical',
        userId: currentUserId,
        sparAnswers: serializedAnswers,
      });

      if (result.data.success && result.data.analysis) {
        // Store AI-generated insights back onto the user's own analysis document
        // The document was just created in finalizeUserReflection — find it by connectionId + userId
        const q = query(
          collection(db, 'connectionFlowAnalyses'),
          where('connectionId', '==', connectionId),
          where('userId', '==', currentUserId),
          orderBy('createdAt', 'desc'),
          fsLimit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          await updateDoc(snap.docs[0].ref, {
            aiInsights: result.data.analysis,
            aiModel: 'gpt-4o',
            updatedAt: serverTimestamp(),
          });
        }
      }
    } catch (err) {
      // Non-critical: data was already saved, AI enrichment is best-effort
      console.warn('[PhysicalMeeting] AI analysis failed (non-critical):', err);
    }
  };

  // Finalise this user's reflection:
  //  1. Write a per-user document to connectionFlowAnalyses (no race condition — each user owns their doc)
  //  2. Trigger background AI analysis via Cloud Function
  //  3. Atomic transaction: mark physicalMeeting.status = 'done' only when BOTH users are done
  const finalizeUserReflection = async (answers: typeof sparAnswers | null) => {
    try {
      // 1. Per-user analysis document
      await addDoc(collection(db, 'connectionFlowAnalyses'), {
        connectionId,
        userId: currentUserId,
        otherUserId,
        otherUserName,
        flowSummary: {
          physicalSummary: `Fysieke meeting op ${selectedLocation?.location.address || 'onbekende locatie'}`,
          location: selectedLocation?.location.address || '',
        },
        sparAnswers: answers || {},
        aiInsights: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // 2. Non-blocking AI enrichment (runs in background)
      if (answers) {
        triggerAIAnalysis(answers).catch(() => {/* already logged inside */});
      }

      // 3. Atomic transition: 'completed' → 'done' only when both users finished
      await runTransaction(db, async (tx) => {
        const connSnap = await tx.get(connectionRef);
        const connData = connSnap.data();
        const ai = connData?.physicalMeeting?.aiRelationManager;

        const bothDone =
          (ai?.user1?.completed || ai?.user1?.skipped) &&
          (ai?.user2?.completed || ai?.user2?.skipped);

        if (bothDone && connData?.physicalMeeting?.status === 'completed') {
          tx.update(connectionRef, { 'physicalMeeting.status': 'done' });
        }
      });

      setStatus('done');
    } catch (error) {
      console.error('[PhysicalMeeting] Error finalizing reflection:', error);
    }
  };
  
  // Calculate distance and travel time for all proposals
  const proposalDistances = useMemo(() => {
    const result: Record<string, { distance: number; travelTime: number }> = {};
    
    // Get current user location (fallback to user1 or user2 based on isUser1)
    const myLocation = isUser1 ? userLocations.user1 : userLocations.user2;
    
    if (!myLocation) return result;
    
    // Calculate for current user proposals
    currentUserProposals.forEach(proposal => {
      const distance = Math.sqrt(
        Math.pow((proposal.lat - myLocation.lat) * 111, 2) + 
        Math.pow((proposal.lng - myLocation.lng) * 111, 2)
      );
      
      // Estimate travel time based on distance
      let averageSpeed = 50;
      if (distance < 10) {
        averageSpeed = 30; // City speed
      } else if (distance > 50) {
        averageSpeed = 80; // Highway speed
      }
      const travelTime = Math.round((distance / averageSpeed) * 60);
      
      result[proposal.id] = { distance, travelTime };
    });
    
    // Calculate for other user proposals
    otherUserProposals.forEach(proposal => {
      const distance = Math.sqrt(
        Math.pow((proposal.lat - myLocation.lat) * 111, 2) + 
        Math.pow((proposal.lng - myLocation.lng) * 111, 2)
      );
      
      let averageSpeed = 50;
      if (distance < 10) {
        averageSpeed = 30;
      } else if (distance > 50) {
        averageSpeed = 80;
      }
      const travelTime = Math.round((distance / averageSpeed) * 60);
      
      result[proposal.id] = { distance, travelTime };
    });
    
    return result;
  }, [currentUserProposals, otherUserProposals, userLocations, isUser1]);
  
  // Helper: shared map props for planning phase (memoized to prevent map flickering on drag)
  const planningMapUserLocations = useMemo(() => [
    ...(userLocations.user1 ? [{ 
      userId: 'user1', 
      userName: isUser1 ? currentUserName : otherUserName, 
      lat: userLocations.user1.lat, 
      lng: userLocations.user1.lng, 
      address: userLocations.user1.address,
      photoURL: isUser1 ? currentUserProfile?.photoURL : otherUserProfile?.photoURL,
      locationAccuracy: (isUser1 ? locationAccuracy : (userLocations.user1 as any)?.accuracy) as 'high' | 'medium' | 'low' | 'unknown' | undefined,
    }] : []),
    ...(userLocations.user2 ? [{ 
      userId: 'user2', 
      userName: isUser1 ? otherUserName : currentUserName, 
      lat: userLocations.user2.lat, 
      lng: userLocations.user2.lng, 
      address: userLocations.user2.address,
      photoURL: isUser1 ? otherUserProfile?.photoURL : currentUserProfile?.photoURL,
      locationAccuracy: (!isUser1 ? locationAccuracy : (userLocations.user2 as any)?.accuracy) as 'high' | 'medium' | 'low' | 'unknown' | undefined,
    }] : [])
  ], [userLocations.user1, userLocations.user2, isUser1, currentUserName, otherUserName, currentUserProfile?.photoURL, otherUserProfile?.photoURL, locationAccuracy]);

  const planningMapProposals = useMemo(() => [
    ...user1Proposals.map(p => ({ 
      id: p.id, lat: p.lat, lng: p.lng, address: p.address, name: p.name, 
      matchQuality: undefined as 'best' | 'good' | 'not_efficient' | undefined, 
      user1Distance: 0, user2Distance: 0, user1TravelTime: 0, user2TravelTime: 0,
      proposedBy: 'user1' as const,
      isFavorite: !isUser1 ? preferredLocationIds.has(p.id) : otherPreferredIds.has(p.id),
      isMutual: preferredLocationIds.has(p.id) && otherPreferredIds.has(p.id),
      draggable: isUser1 === true, // Only owner can drag their own proposals
    })),
    ...user2Proposals.map(p => ({ 
      id: p.id, lat: p.lat, lng: p.lng, address: p.address, name: p.name, 
      matchQuality: undefined as 'best' | 'good' | 'not_efficient' | undefined, 
      user1Distance: 0, user2Distance: 0, user1TravelTime: 0, user2TravelTime: 0,
      proposedBy: 'user2' as const,
      isFavorite: isUser1 ? preferredLocationIds.has(p.id) : otherPreferredIds.has(p.id),
      isMutual: preferredLocationIds.has(p.id) && otherPreferredIds.has(p.id),
      draggable: isUser1 === false, // Only owner can drag their own proposals
    }))
  ], [user1Proposals, user2Proposals, isUser1, preferredLocationIds, otherPreferredIds]);

  // ── Route lines for the planning map ─────────────────────────────────────
  // Shows a dashed line between the two users so they can see how far apart
  // they are while proposing meeting locations.
  const planningMapRouteLines = useMemo(() => {
    if (!userLocations.user1 || !userLocations.user2) return [];
    return [
      {
        from: { lat: userLocations.user1.lat, lng: userLocations.user1.lng },
        to:   { lat: userLocations.user2.lat, lng: userLocations.user2.lng },
        color: '#94A3B8', // slate-400 — neutral, doesn't compete with markers
        label: 'Afstand tussen jullie',
      },
    ];
  }, [userLocations.user1, userLocations.user2]);

  // ── User locations for the navigation/scheduled map ───────────────────────
  // Memoized so the MapContainer doesn't re-mount on every parent render.
  const navigationMapUserLocations = useMemo(() => [
    ...(userLocations.user1 ? [{
      userId: 'user1',
      userName: isUser1 ? currentUserName : otherUserName,
      lat: userLocations.user1.lat,
      lng: userLocations.user1.lng,
      address: userLocations.user1.address,
      photoURL: isUser1 ? currentUserProfile?.photoURL : otherUserProfile?.photoURL,
    }] : []),
    ...(userLocations.user2 ? [{
      userId: 'user2',
      userName: isUser1 ? otherUserName : currentUserName,
      lat: userLocations.user2.lat,
      lng: userLocations.user2.lng,
      address: userLocations.user2.address,
      photoURL: isUser1 ? otherUserProfile?.photoURL : currentUserProfile?.photoURL,
    }] : []),
  ], [userLocations.user1, userLocations.user2, isUser1, currentUserName, otherUserName, currentUserProfile?.photoURL, otherUserProfile?.photoURL]);

  // ── Route lines for the navigation/scheduled map ──────────────────────────
  // Fetches road-following geometry from OSRM; falls back to straight lines.
  // useState so we can update it asynchronously after OSRM responds.
  const [navigationMapRouteLines, setNavigationMapRouteLines] = useState<Array<{
    from: { lat: number; lng: number };
    to: { lat: number; lng: number };
    color: string;
    label?: string;
    geometry?: Array<[number, number]>;
  }>>([]);

  useEffect(() => {
    if (!selectedLocation) { setNavigationMapRouteLines([]); return; }
    const dest = { lat: selectedLocation.location.lat, lng: selectedLocation.location.lng };
    if (!dest.lat || !dest.lng) { setNavigationMapRouteLines([]); return; }

    // Build base lines (straight-line fallback) immediately for instant rendering
    const baseLines = [];
    if (userLocations.user1) {
      baseLines.push({
        from:  { lat: userLocations.user1.lat, lng: userLocations.user1.lng },
        to:    dest,
        color: isUser1 ? '#3B82F6' : '#F97316',
        label: isUser1 ? currentUserName : otherUserName,
      });
    }
    if (userLocations.user2) {
      baseLines.push({
        from:  { lat: userLocations.user2.lat, lng: userLocations.user2.lng },
        to:    dest,
        color: isUser1 ? '#F97316' : '#3B82F6',
        label: isUser1 ? otherUserName : currentUserName,
      });
    }
    setNavigationMapRouteLines(baseLines);

    // Fetch real-road geometry from OSRM and update lines when ready
    let cancelled = false;
    (async () => {
      try {
        const { calculateRoute } = await import('../../services/routingService');
        const enriched = await Promise.all(
          baseLines.map(async (line) => {
            try {
              const result = await calculateRoute(line.from.lat, line.from.lng, line.to.lat, line.to.lng);
              return result.geometry ? { ...line, geometry: result.geometry } : line;
            } catch {
              return line;
            }
          })
        );
        if (!cancelled) setNavigationMapRouteLines(enriched);
      } catch {
        // OSRM unavailable — straight lines remain
      }
    })();

    return () => { cancelled = true; };
  }, [
    selectedLocation,
    userLocations.user1?.lat, userLocations.user1?.lng,
    userLocations.user2?.lat, userLocations.user2?.lng,
    isUser1, currentUserName, otherUserName,
  ]);

  // Determine if matches tab should show
  const hasEnoughProposals = currentUserProposals.length >= 3 && otherUserProposals.length >= 3;

  // Determine guided flow step
  // Note: selectedLocation check must come BEFORE bestMatches empty check,
  // because when returning from a saved planning session, selectedLocation is
  // restored from Firestore but bestMatches may be empty.
  const guidedStep = (() => {
    if (selectedLocation && status === 'planning') return 'plan_datetime';
    if (currentUserProposals.length < 3) return 'add_locations';
    if (otherUserProposals.length < 3) return 'wait_other';
    if (preferredLocationIds.size === 0 && otherUserProposals.length >= 3) return 'pick_favorites';
    if (bestMatches.length === 0 && hasEnoughProposals) return 'calculate_matches';
    if (bestMatches.length > 0 && !selectedLocation) return 'select_match';
    return 'done';
  })();

  // Determine which tabs should pulse
  const shouldPulseKaart = currentUserProposals.length + otherUserProposals.length > 0 && planningTab !== 'kaart';
  const shouldPulseMatches = (bestMatches.length > 0 || (hasEnoughProposals && preferredLocationIds.size > 0)) && planningTab !== 'matches';

  // Handle dragging a proposal marker on the map (stable ref to prevent map re-render)
  // Must be declared at top level (not inside conditional) to respect React hooks rules
  const handleLocationDragged = useCallback(async (id: string, lat: number, lng: number) => {
    try {
      const address = await reverseGeocode(lat, lng) || 'Aangepaste locatie';
      const connectionDoc = await getDoc(connectionRef);
      const data = connectionDoc.data();
      if (!data) return;
      const userKey = currentUserId === data.user1Id ? 'user1' : 'user2';
      const myProposals = userKey === 'user1' ? [...user1Proposals] : [...user2Proposals];
      const idx = myProposals.findIndex(p => p.id === id);
      if (idx === -1) return;
      myProposals[idx] = { ...myProposals[idx], lat, lng, address, name: myProposals[idx].name || address };
      await updateDoc(connectionRef, { [`physicalMeeting.locationProposals.${userKey}`]: myProposals });
      if (userKey === 'user1') { setUser1Proposals(myProposals); } else { setUser2Proposals(myProposals); }
      // Clear matches since locations changed
      if (bestMatches.length > 0) {
        setBestMatches([]);
        success('Locatie verplaatst — bereken matches opnieuw');
      } else {
        success('Locatie verplaatst');
      }
    } catch (error) {
      console.error('Error updating dragged location:', error);
    }
  }, [connectionRef, currentUserId, user1Proposals, user2Proposals, bestMatches.length]);

  // Render based on status
  if (status === 'planning') {

    // ─── Shortcut: location already chosen → show focused "plan datum & tijd" screen ───
    if (selectedLocation && guidedStep === 'plan_datetime') {
      return (
        <>
          <ToastContainer toasts={toasts} onClose={removeToast} />
          <div className="w-full max-w-xl mx-auto px-4 py-6 flex flex-col gap-4">

            {/* Progress indicator */}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                Locatie gekozen
              </span>
              <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              <span className="text-indigo-600 font-semibold">Datum & tijd plannen</span>
              <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              <span className="text-gray-400">Navigeren</span>
            </div>

            {/* Location confirmed card */}
            <div className="bg-white rounded-2xl border border-green-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-green-50 border-b border-green-100 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <span className="text-sm font-semibold text-green-800">Ontmoetingslocatie bevestigd</span>
              </div>
              <div className="px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{selectedLocation.location.name || 'Ontmoetingslocatie'}</p>
                  <p className="text-xs text-gray-400 truncate">{selectedLocation.location.address}</p>
                  {selectedLocation.totalDistance != null && (
                    <p className="text-xs text-gray-400">{selectedLocation.totalDistance.toFixed(1)} km totale reisafstand</p>
                  )}
                </div>
                <button
                  onClick={() => { setSelectedLocation(null); setBestMatches([]); }}
                  className="text-xs text-gray-400 hover:text-gray-600 flex-shrink-0 px-2 py-1 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Wijzig
                </button>
              </div>
            </div>

            {/* Main CTA — plan date & time */}
            <div className="bg-white rounded-2xl border border-indigo-200 shadow-sm p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Kies een datum en tijd</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Stel samen met {otherUserName} 3 tijdsblokken voor — de eerste wederzijdse keuze wordt de afspraak.
                  </p>
                </div>
              </div>
              {onOpenAgenda ? (
                <button
                  onClick={() => selectLocation(selectedLocation)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: 14,
                    borderRadius: 12,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    boxShadow: '0 4px 14px rgba(79,70,229,0.35)',
                    transition: 'transform 0.1s, box-shadow 0.1s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(79,70,229,0.5)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 14px rgba(79,70,229,0.35)'; (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Open agenda &amp; plan datum
                </button>
              ) : (
                <div className="w-full py-3 bg-gray-100 text-gray-400 text-sm font-medium rounded-xl text-center">
                  Wacht op agenda…
                </div>
              )}
            </div>

            {/* Other user status */}
            {otherUserIsPlanning.isPlanning ? (
              <div className="flex items-center gap-2.5 px-4 py-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse flex-shrink-0" />
                <p className="text-xs text-indigo-700">
                  <span className="font-semibold">{otherUserIsPlanning.userName}</span> is ook bezig met plannen
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2.5 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl">
                <div className="w-2 h-2 rounded-full bg-gray-300 flex-shrink-0" />
                <p className="text-xs text-gray-500">
                  {otherUserName} heeft de agenda nog niet geopend
                </p>
              </div>
            )}
          </div>
        </>
      );
    }

    const tabs = [
      { id: 'locaties' as const, label: 'Locaties', badge: `${currentUserProposals.length + otherUserProposals.length}` },
      { id: 'kaart' as const, label: 'Kaart', pulse: shouldPulseKaart },
      ...(hasEnoughProposals || bestMatches.length > 0 ? [{ id: 'matches' as const, label: 'Matches', badge: bestMatches.length > 0 ? `${bestMatches.length}` : undefined, pulse: shouldPulseMatches }] : []),
    ];

    // Guided step banner config
    const stepConfig: Record<string, { icon: string; text: string; color: string }> = {
      add_locations: { icon: '1', text: `Voeg minimaal 3 locatievoorstellen toe`, color: 'blue' },
      wait_other: { icon: '⏳', text: `Wacht tot ${otherUserName} ook 3 locaties heeft toegevoegd`, color: 'gray' },
      pick_favorites: { icon: '2', text: `Tik op locaties van ${otherUserName} om je voorkeur aan te geven`, color: 'purple' },
      calculate_matches: { icon: '3', text: `Bereken de beste ontmoetingslocaties`, color: 'green' },
      select_match: { icon: '4', text: `Kies een match om de afspraak te plannen`, color: 'emerald' },
      plan_datetime: { icon: '📅', text: `Locatie gekozen! Plan nu een datum en tijd via de agenda`, color: 'indigo' },
      done: { icon: '✓', text: `Afspraak gepland`, color: 'green' },
    };
    const currentStep = stepConfig[guidedStep];
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      gray: 'bg-gray-50 border-gray-200 text-gray-600',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    };
    const stepIconColorMap: Record<string, string> = {
      blue: 'bg-blue-500',
      gray: 'bg-gray-400',
      purple: 'bg-purple-500',
      green: 'bg-green-500',
      emerald: 'bg-emerald-500',
      indigo: 'bg-indigo-500',
    };

    // Shared location list content renderer (used in both compact and expanded view)
    const renderLocationContent = () => (
      <div className="space-y-3">
        {/* Search */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            <input
              type="text"
              value={locationSearchQuery}
              onChange={(e) => setLocationSearchQuery(e.target.value)}
              placeholder="Zoek adres of plaatsnaam..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-0 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:bg-white transition-all"
            />
          </div>
          
          {locationSearchResults.length > 0 && (
            <div className="mt-2 max-h-36 overflow-y-auto rounded-xl border border-gray-100">
              {locationSearchResults.map((result) => (
                <button
                  key={`${result.lat}-${result.lng}`}
                  className="w-full text-left px-3 py-2.5 text-sm text-gray-900 hover:bg-blue-50 transition-colors first:rounded-t-xl last:rounded-b-xl truncate"
                  onClick={() => { addLocationProposal(result); setLocationSearchQuery(''); setLocationSearchResults([]); }}
                >
                  {result.address}
                </button>
              ))}
            </div>
          )}
          
          {locationSearchError && <p className="mt-1.5 text-xs text-red-500">{locationSearchError}</p>}
          
          {favoriteLocations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {favoriteLocations.map((fav) => (
                <button
                  key={fav.id}
                  onClick={() => { addLocationProposal({ lat: fav.coordinates.lat, lng: fav.coordinates.lng, address: fav.address }, fav.name); }}
                  disabled={loadingStates.addingProposal}
                  className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium rounded-full transition-colors disabled:opacity-50"
                >
                  {fav.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* My proposals */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Mijn locaties</span>
            {currentUserProposals.length > 0 && currentUserProposals.length < 3 && (
              <span className="text-xs text-amber-600 font-medium">Nog {3 - currentUserProposals.length} nodig</span>
            )}
          </div>
          
          {currentUserProposals.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-gray-400">Zoek hierboven om locaties toe te voegen</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {currentUserProposals.map((proposal, index) => {
                const distanceInfo = proposalDistances[proposal.id];
                const isLikedByOther = otherPreferredIds.has(proposal.id);
                return (
                  <div key={proposal.id} className={`flex items-center gap-2.5 px-3 py-2 ${isLikedByOther ? 'bg-green-50/50' : ''}`}>
                    <div className="w-6 h-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{index + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 truncate">{proposal.name}</span>
                        {isLikedByOther && (
                          <span className="flex-shrink-0 w-4 h-4 text-green-500" title={`${otherUserName} vindt deze locatie goed`}>
                            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{proposal.address}{distanceInfo ? ` · ${distanceInfo.distance.toFixed(1)}km` : ''}</div>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const connectionDoc = await getDoc(connectionRef);
                          const data = connectionDoc.data();
                          const userKey = currentUserId === data?.user1Id ? 'user1' : 'user2';
                          const updated = currentUserProposals.filter(p => p.id !== proposal.id);
                          await updateDoc(connectionRef, { [`physicalMeeting.locationProposals.${userKey}`]: updated });
                          if (userKey === 'user1') { setUser1Proposals(updated); } else { setUser2Proposals(updated); }
                          success('Locatie verwijderd');
                        } catch (error) { console.error('Error deleting location:', error); showError('Fout bij verwijderen'); }
                      }}
                      className="p-1.5 text-gray-300 hover:text-red-500 rounded-md transition-colors flex-shrink-0"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Other user proposals - clickable to vote */}
        <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
          guidedStep === 'pick_favorites' && !guideDismissed ? 'border-purple-300 ring-2 ring-purple-100' : 'border-gray-100'
        }`}>
          <div className="px-3 py-2 border-b border-gray-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Van {otherUserName}</span>
            {otherUserProposals.length > 0 && (
              <span className={`text-xs font-medium ${guidedStep === 'pick_favorites' ? 'text-purple-600' : 'text-gray-400'}`}>
                {guidedStep === 'pick_favorites' ? '👆 Tik om te liken' : `${preferredLocationIds.size} geliked`}
              </span>
            )}
          </div>

          {/* First-time hint when user should pick favorites */}
          {guidedStep === 'pick_favorites' && !guideDismissed && otherUserProposals.length > 0 && preferredLocationIds.size === 0 && (
            <div className="px-3 py-2 bg-purple-50 border-b border-purple-100 flex items-center gap-2">
              <span className="text-purple-500 animate-bounce">👆</span>
              <p className="text-xs text-purple-700 flex-1">Tik op een locatie hieronder om aan te geven welke jij het liefst hebt. De ander ziet ook jouw voorkeur!</p>
              <button onClick={() => setGuideDismissed(true)} className="text-purple-400 hover:text-purple-600 flex-shrink-0">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          )}

          {otherUserProposals.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-gray-400">Wacht op locaties van {otherUserName}</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {otherUserProposals.map((proposal, index) => {
                const distanceInfo = proposalDistances[proposal.id];
                const isPreferred = preferredLocationIds.has(proposal.id);
                const isMutual = isPreferred && otherPreferredIds.has(proposal.id);
                return (
                  <button
                    key={proposal.id}
                    onClick={() => toggleLocationPreference(proposal.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all group ${
                      isMutual ? 'bg-green-50' : isPreferred ? 'bg-blue-50/50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all ${
                      isPreferred ? 'bg-blue-500 text-white' : 'bg-orange-50 text-orange-600 group-hover:bg-purple-100 group-hover:text-purple-600'
                    }`}>
                      {isPreferred 
                        ? <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                        : <span>{index + 1}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900 truncate">{proposal.name}</span>
                        {isMutual && (
                          <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full flex-shrink-0 animate-pulse">Match!</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{proposal.address}{distanceInfo ? ` · ${distanceInfo.distance.toFixed(1)}km` : ''}</div>
                    </div>
                    <div className={`flex-shrink-0 transition-all ${isPreferred ? 'text-blue-500 scale-110' : 'text-gray-300 group-hover:text-purple-400'}`}>
                      <svg className="w-5 h-5" fill={isPreferred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={isPreferred ? 0 : 1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA if both have 3+ */}
        {hasEnoughProposals && bestMatches.length === 0 && (
          <button
            onClick={() => { handleCalculateBestMatches(); setPlanningTab('matches'); }}
            disabled={loadingStates.calculatingMatches}
            className={`w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold text-sm rounded-xl hover:from-green-600 hover:to-emerald-700 active:scale-[0.98] disabled:opacity-50 transition-all shadow-md shadow-green-500/20 ${
              guidedStep === 'calculate_matches' ? 'animate-pulse' : ''
            }`}
          >
            {loadingStates.calculatingMatches ? 'Berekenen...' : 'Bereken beste matches'}
          </button>
        )}
      </div>
    );

    // Shared map content renderer
    const renderMapContent = (mapHeight: number = 260) => (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <EnhancedLocationMap
          userLocations={planningMapUserLocations}
          proposedLocations={planningMapProposals}
          selectedLocation={selectedLocation ? { lat: selectedLocation.location.lat, lng: selectedLocation.location.lng, address: selectedLocation.location.address } : undefined}
          routeLines={planningMapRouteLines}
          height={mapHeight}
          autoFitBounds={true}
          onLocationDragged={handleLocationDragged}
        />
        <div className="px-3 py-2 flex items-center justify-between border-t border-gray-50">
          <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
            {/* User 1 (current user) — always blue */}
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded-full bg-blue-100 border-2 border-blue-500 flex items-center justify-center text-[7px] font-bold text-blue-700">
                {currentUserName.charAt(0).toUpperCase()}
              </span>
              <span className="font-medium">{currentUserName.split('@')[0].split(' ')[0]}</span>
            </span>
            {/* User 2 (other user) — always orange */}
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 rounded-full bg-orange-100 border-2 border-orange-500 flex items-center justify-center text-[7px] font-bold text-orange-700">
                {otherUserName.charAt(0).toUpperCase()}
              </span>
              <span className="font-medium">{otherUserName.split('@')[0].split(' ')[0]}</span>
            </span>
            {/* Proposed locations indicator */}
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
              Voorstellen
            </span>
            {/* Favoriet indicator (only show when relevant) */}
            {planningMapProposals.some(l => l.isFavorite && !l.isMutual) && (
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                Favoriet
              </span>
            )}
            {/* Match indicator (only show when relevant) */}
            {planningMapProposals.some(l => l.isMutual) && (
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                Match
              </span>
            )}
          </div>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={locationSharingEnabled} onChange={(e) => setLocationSharingEnabled(e.target.checked)} className="w-3 h-3 rounded border-gray-300 text-blue-600" />
            <span className="text-xs text-gray-500">GPS</span>
          </label>
        </div>
      </div>
    );

    return (
      <>
        <ToastContainer toasts={toasts} onClose={removeToast} />

        {/* ===== OTHER USER IS PLANNING OVERLAY ===== */}
        {otherUserIsPlanning.isPlanning && (
          <div className="w-full max-w-xl mx-auto px-3 py-4">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-200 shadow-sm p-6 text-center">
              {/* Animated planning indicator */}
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <svg className="w-8 h-8 text-white animate-pulse" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-1">
                {otherUserIsPlanning.userName} plant de afspraak
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                De locatie <span className="font-semibold text-indigo-700">"{otherUserIsPlanning.locationName}"</span> is gekozen. 
                {otherUserIsPlanning.userName} is nu een datum en tijd aan het kiezen in de agenda.
              </p>
              
              {/* Location card */}
              {selectedLocation && (
                <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 text-left">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{selectedLocation.location.name || 'Ontmoetingslocatie'}</p>
                      <p className="text-xs text-gray-400 truncate">{selectedLocation.location.address}</p>
                    </div>
                    <span className="text-green-500 flex-shrink-0">✓</span>
                  </div>
                </div>
              )}
              
              {/* Waiting animation dots */}
              <div className="flex items-center justify-center gap-1.5 mb-3">
                <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              
              <p className="text-xs text-gray-400">
                Je ontvangt een melding zodra de afspraak is ingepland
              </p>
            </div>
          </div>
        )}

        {/* ===== EXPANDED FULLSCREEN VIEW ===== */}
        {showExpandedView && !otherUserIsPlanning.isPlanning && (
          <div className="fixed inset-0 z-[1000] bg-white flex flex-col">
            {/* Expanded header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <h2 className="text-base font-semibold text-gray-900">Fysieke Afspraak — Overzicht</h2>
              </div>
              <button
                onClick={() => setShowExpandedView(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Expanded content - horizontal layout */}
            <div className="flex-1 flex gap-4 p-4 overflow-hidden">
              {/* Left: Location lists */}
              <div className="w-1/3 overflow-y-auto min-w-0 scrollbar-subtle">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Locaties</h3>
                {renderLocationContent()}
              </div>

              {/* Center: Map */}
              <div className="flex-1 min-w-0 flex flex-col">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Kaart</h3>
                <div className="flex-1">
                  {renderMapContent(500)}
                </div>
              </div>

              {/* Right: Matches */}
              <div className="w-1/4 overflow-y-auto min-w-0 scrollbar-subtle">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Matches ({bestMatches.length})</h3>
                {bestMatches.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
                    <p className="text-sm text-gray-500">Nog geen matches</p>
                    {hasEnoughProposals && (
                      <button
                        onClick={handleCalculateBestMatches}
                        disabled={loadingStates.calculatingMatches}
                        className="mt-3 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50"
                      >
                        {loadingStates.calculatingMatches ? 'Berekenen...' : 'Bereken matches'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Notice when another user already confirmed a location */}
                    {selectedLocation && guidedStep !== 'select_match' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'linear-gradient(135deg, #dcfce7, #d1fae5)', border: '1.5px solid #86efac', borderRadius: 10, marginBottom: 4 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>
                          {otherUserIsPlanning.isPlanning
                            ? `${otherUserIsPlanning.userName} heeft "${selectedLocation.location.name || selectedLocation.location.address}" gekozen`
                            : `Locatie bevestigd: "${selectedLocation.location.name || selectedLocation.location.address}"`}
                        </span>
                      </div>
                    )}
                    {bestMatches.map((match, index) => {
                      const isSelected = selectedLocation?.locationId === match.locationId;
                      const isLockedOut = !!selectedLocation && !isSelected;
                      return (
                        <button
                          key={match.locationId}
                          onClick={() => !isLockedOut && setSelectedLocation(match)}
                          disabled={isLockedOut}
                          style={isLockedOut ? { opacity: 0.35, filter: 'grayscale(0.6)', cursor: 'not-allowed', pointerEvents: 'none' } : {}}
                          className={`w-full text-left p-3 rounded-xl border transition-all ${
                            isSelected
                              ? 'border-green-500 bg-green-50 ring-1 ring-green-400'
                              : 'border-gray-100 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-5 h-5 rounded-full text-white flex items-center justify-center text-xs font-bold ${
                              match.matchQuality === 'best' ? 'bg-green-500' : match.matchQuality === 'good' ? 'bg-yellow-500' : 'bg-red-500'
                            }`}>{index + 1}</span>
                            <span className="text-sm font-medium text-gray-900 truncate">{match.location.name || match.location.address}</span>
                            {isSelected && (
                              <span className="ml-auto flex items-center gap-1 text-green-600 text-xs font-semibold">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                                Gekozen
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 truncate pl-7">{match.location.address}</p>
                        </button>
                      );
                    })}
                    
                    {/* Confirm selection button — only if not yet confirmed */}
                    {selectedLocation && guidedStep === 'select_match' && (
                      <button
                        onClick={() => selectLocation(selectedLocation)}
                        className="w-full mt-2 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm rounded-xl hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all shadow-md shadow-blue-500/20 flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        Bevestig "{selectedLocation.location.name || selectedLocation.location.address}"
                      </button>
                    )}
                    
                    {/* Plan date/time button — shown after location is confirmed */}
                    {guidedStep === 'plan_datetime' && selectedLocation && onOpenAgenda && (
                      <div className="mt-3 bg-indigo-50 rounded-xl border border-indigo-200 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-green-500">✓</span>
                          <span className="text-sm font-medium text-gray-900 truncate">{selectedLocation.location.name || selectedLocation.location.address}</span>
                        </div>
                        <button
                          onClick={() => selectLocation(selectedLocation)}
                          className="w-full py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-sm rounded-xl hover:from-indigo-700 hover:to-purple-700 active:scale-[0.98] transition-all shadow-md shadow-indigo-500/20 flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          Plan datum & tijd
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== COMPACT VIEW (default) - hidden when expanded or other user is planning ===== */}
        {!showExpandedView && !otherUserIsPlanning.isPlanning && (
        <div className="w-full max-w-xl mx-auto px-3 py-2 flex flex-col" style={{ maxHeight: 'min(480px, calc(100vh - 320px))' }}>
          
          {/* Header row - ultra compact */}
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <h2 className="text-sm font-semibold text-gray-900">Fysieke Afspraak</h2>
            </div>
            <div className="flex items-center gap-1.5">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${currentUserProposals.length >= 3 ? 'bg-green-100 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                Jij {currentUserProposals.length}/3
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${otherUserProposals.length >= 3 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {otherUserName} {otherUserProposals.length}/3
              </span>
              {/* Expand button */}
              <button
                onClick={() => setShowExpandedView(true)}
                className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                title="Vergroot overzicht"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
              </button>
            </div>
          </div>

          {/* Guided step banner */}
          {guidedStep !== 'done' && (
            <div className={`flex items-center gap-2 px-3 py-2 mb-2 rounded-xl border flex-shrink-0 ${colorMap[currentStep.color]}`}>
              <div className={`w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${stepIconColorMap[currentStep.color]}`}>
                {currentStep.icon}
              </div>
              <p className="text-xs font-medium flex-1">{currentStep.text}</p>
              {guidedStep === 'pick_favorites' && (
                <button
                  onClick={() => setPlanningTab('locaties')}
                  className="text-xs font-semibold text-purple-600 hover:text-purple-800 flex-shrink-0"
                >
                  Ga naar locaties →
                </button>
              )}
              {guidedStep === 'calculate_matches' && (
                <button
                  onClick={() => { handleCalculateBestMatches(); setPlanningTab('matches'); }}
                  className="text-xs font-semibold text-green-600 hover:text-green-800 flex-shrink-0"
                >
                  Bereken →
                </button>
              )}
              {guidedStep === 'select_match' && planningTab !== 'matches' && (
                <button
                  onClick={() => setPlanningTab('matches')}
                  className="text-xs font-semibold text-emerald-600 hover:text-emerald-800 flex-shrink-0"
                >
                  Bekijk matches →
                </button>
              )}
              {guidedStep === 'plan_datetime' && onOpenAgenda && (
                <button
                  onClick={() => selectedLocation ? selectLocation(selectedLocation) : onOpenAgenda()}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                >
                  Open agenda →
                </button>
              )}
            </div>
          )}

          {/* Location selected card — shown when location is chosen but not yet scheduled */}
          {guidedStep === 'plan_datetime' && selectedLocation && (
            <div className="mb-2 bg-white rounded-2xl border border-indigo-200 shadow-sm p-3 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{selectedLocation.location.name || 'Ontmoetingslocatie'}</p>
                  <p className="text-xs text-gray-400 truncate">{selectedLocation.location.address}</p>
                </div>
                <span className="text-green-500 text-lg flex-shrink-0">✓</span>
              </div>
              {onOpenAgenda && (
                <button
                  onClick={() => selectLocation(selectedLocation)}
                  className="w-full mt-3 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-sm rounded-xl hover:from-indigo-700 hover:to-purple-700 active:scale-[0.98] transition-all shadow-md shadow-indigo-500/20 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Plan datum & tijd
                </button>
              )}
            </div>
          )}

          {/* Tab bar - compact with pulse animation */}
          <div className="flex bg-gray-100 rounded-lg p-0.5 mb-2 flex-shrink-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setPlanningTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium rounded-md transition-all relative ${
                  planningTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.badge && (
                  <span className={`text-[10px] px-1 py-0 rounded-full ${
                    planningTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'
                  }`}>{tab.badge}</span>
                )}
                {/* Pulse dot for tabs needing attention */}
                {'pulse' in tab && tab.pulse && planningTab !== tab.id && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Location accuracy warning */}
          {(locationAccuracy === 'low' || locationAccuracy === 'unknown') && (
            <div className="flex items-start gap-2 px-3 py-2 mb-2 bg-amber-50 border border-amber-200 rounded-xl flex-shrink-0">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-800">Locatie mogelijk onnauwkeurig</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  {locationAccuracy === 'unknown' 
                    ? 'Locatie kon niet bepaald worden. Mogelijk gebruik je incognito modus of een VPN. Stel je kantooradres in via Profiel instellingen voor nauwkeurige locatie.'
                    : 'Je locatie is gebaseerd op IP-adres en kan onnauwkeurig zijn (bijv. door VPN). Stel je kantooradres in via Profiel instellingen.'}
                </p>
              </div>
            </div>
          )}

          {/* Tab content - fills remaining space, scrolls internally */}
          <div className="flex-1 overflow-y-auto min-h-0 scrollbar-subtle">
            
            {/* === TAB: LOCATIES === */}
            {planningTab === 'locaties' && renderLocationContent()}

            {/* === TAB: KAART === */}
            {planningTab === 'kaart' && renderMapContent()}

            {/* === TAB: MATCHES === */}
            {planningTab === 'matches' && (
              <div className="space-y-3">
                {loadingStates.calculatingMatches ? (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
                    <svg className="animate-spin w-6 h-6 text-blue-600 mx-auto mb-2" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                    <p className="text-sm text-gray-500">Berekenen...</p>
                  </div>
                ) : bestMatches.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
                    <p className="text-sm text-gray-500 mb-3">Nog geen matches berekend</p>
                    {hasEnoughProposals && (
                      <button
                        onClick={handleCalculateBestMatches}
                        disabled={loadingStates.calculatingMatches}
                        className="px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-all"
                      >
                        Bereken nu
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {/* Selected location banner */}
                    {selectedLocation && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'linear-gradient(135deg, #dcfce7, #d1fae5)', borderBottom: '1px solid #bbf7d0' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d' }}>
                          {otherUserIsPlanning.isPlanning
                            ? `${otherUserIsPlanning.userName} koos "${selectedLocation.location.name || selectedLocation.location.address}"`
                            : `Gekozen: "${selectedLocation.location.name || selectedLocation.location.address}"`}
                        </span>
                      </div>
                    )}
                    <div className="divide-y divide-gray-50">
                      {bestMatches.map((match, index) => {
                        const isSelected = selectedLocation?.locationId === match.locationId;
                        const isLockedOut = !!selectedLocation && !isSelected;
                        return (
                        <div
                          key={match.locationId}
                          style={isLockedOut ? { opacity: 0.3, filter: 'grayscale(0.7)', pointerEvents: 'none' } : {}}
                          className={`flex items-center gap-3 px-3 py-3 transition-colors ${
                            isSelected ? 'bg-green-50' : ''
                          }`}
                        >
                          <span className={`w-6 h-6 rounded-full text-white flex items-center justify-center text-xs font-bold flex-shrink-0 ${match.matchQuality === 'best' ? 'bg-green-500' : match.matchQuality === 'good' ? 'bg-amber-500' : 'bg-red-400'}`}>{index + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{match.location.name || match.location.address}</div>
                            <div className="text-xs text-gray-400">{match.user1Distance.toFixed(1)}km + {match.user2Distance.toFixed(1)}km = {match.totalDistance.toFixed(1)}km</div>
                          </div>
                          {isSelected ? (
                            <span className="px-3 py-1.5 bg-green-100 text-green-700 text-xs font-semibold rounded-lg flex-shrink-0 flex items-center gap-1">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                              Gekozen
                            </span>
                          ) : !selectedLocation ? (
                            <button onClick={() => selectLocation(match)} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 active:scale-95 transition-all flex-shrink-0">
                              Kies & Plan
                            </button>
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                    
                    {/* Plan date/time button in compact matches tab */}
                    {guidedStep === 'plan_datetime' && selectedLocation && onOpenAgenda && (
                      <div className="p-3 border-t border-gray-100">
                        <button
                          onClick={onOpenAgenda}
                          className="w-full py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-sm rounded-xl hover:from-indigo-700 hover:to-purple-700 active:scale-[0.98] transition-all shadow-md shadow-indigo-500/20 flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          Plan datum & tijd
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </>
    );
  }
  
  // ============================================================
  // UNIFIED POST-PLANNING FLOW: scheduled → navigation → active → completed → done
  // Single CTA button that evolves through the flow steps
  // ============================================================
  if (status === 'scheduled' || status === 'navigation' || status === 'active' || status === 'completed' || status === 'done') {
    const navKey = getUserKey();
    const myNav = navigationStatus[navKey];

    // Determine the current flow step
    type FlowStep = 'gepland' | 'navigeer' | 'aangekomen' | 'meeting' | 'einde_meeting' | 'ai_reflectie' | 'voltooid';
    let currentFlowStep: FlowStep = 'gepland';
    if (status === 'done') currentFlowStep = 'voltooid';
    else if (status === 'completed' && showAIRelationManager) currentFlowStep = 'ai_reflectie';
    else if (status === 'completed') currentFlowStep = 'voltooid';
    else if (status === 'active') currentFlowStep = 'meeting';
    else if ((status === 'scheduled' || status === 'navigation') && myNav?.arrived) currentFlowStep = 'aangekomen';
    else if ((status === 'scheduled' || status === 'navigation') && myNav?.started) currentFlowStep = 'navigeer';
    else currentFlowStep = 'gepland';

    // Step definitions
    const flowSteps: { id: FlowStep; label: string; icon: string }[] = [
      { id: 'gepland', label: 'Gepland', icon: '📅' },
      { id: 'navigeer', label: 'Onderweg', icon: '🧭' },
      { id: 'aangekomen', label: 'Aangekomen', icon: '📍' },
      { id: 'meeting', label: 'Meeting', icon: '🤝' },
      { id: 'ai_reflectie', label: 'Reflectie', icon: '🤖' },
      { id: 'voltooid', label: 'Voltooid', icon: '✓' },
    ];
    const currentStepIndex = flowSteps.findIndex(s => s.id === currentFlowStep);

    // Guide text per step
    const stepGuides: Record<FlowStep, string> = {
      gepland: appointmentDateTime
        ? `Afspraak gepland op ${appointmentDateTime.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} om ${appointmentDateTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}. Gebruik de navigatieknop om naar de locatie te gaan.`
        : `Je afspraak met ${otherUserName} is gepland. Gebruik de navigatieknop wanneer je onderweg gaat.`,
      navigeer: `Je bent onderweg naar ${selectedLocation?.location.name || 'de locatie'}. Tik op "Aangekomen" zodra je er bent.`,
      aangekomen: `Je bent er! Start de meeting wanneer jullie allebei klaar zijn.`,
      meeting: `Jullie meeting loopt. Veel succes! Tik op "Einde meeting" wanneer jullie klaar zijn.`,
      einde_meeting: `De meeting is afgelopen. Ga door naar de AI Relation Manager om te reflecteren.`,
      ai_reflectie: `De meeting is voorbij. Reflecteer met de AI Relation Manager voor betere follow-up.`,
      voltooid: `Je fysieke afspraak is succesvol afgerond!`,
    };

    // Extract expertise tags from the other user's profile for personalised questions
    const otherExpertiseTags: string[] = (
      otherUserProfile?.expertise ||
      otherUserProfile?.interests ||
      otherUserProfile?.tags ||
      []
    ).slice(0, 3) as string[];
    const expertiseLabel = otherExpertiseTags.length > 0
      ? otherExpertiseTags.join(', ')
      : null;
    const otherCompany = otherUserProfile?.company || null;

    // Spar questions — focused on building expert status for the OTHER user.
    // Labels are personalised when expertise/company data is available.
    const sparQuestions = [
      {
        key: 'feeling' as const,
        label: `Hoe verliep het gesprek met ${otherUserName}?`,
        placeholder: 'Beschrijf hoe het gesprek verliep, de sfeer en het contact...',
        required: true,
      },
      {
        key: 'highlights' as const,
        label: expertiseLabel
          ? `Welke expertise van ${otherUserName} viel je op? (${expertiseLabel})`
          : `Welke kennis of expertise van ${otherUserName} viel je op?`,
        placeholder: expertiseLabel
          ? `Bijv. hun aanpak rondom ${otherExpertiseTags[0]}, concrete voorbeelden...`
          : 'Concrete voorbeelden van hun vakkennis, inzichten of aanpak...',
        required: true,
      },
      {
        key: 'experience' as const,
        label: otherCompany
          ? `Hoe beoordeel je de expertise van ${otherUserName} (${otherCompany})?`
          : `Hoe beoordeel je de expertise van ${otherUserName}?`,
        placeholder: 'Beschrijf hun niveau, sterktes en wat jou opviel aan hun vakkennis...',
        required: true,
      },
      {
        key: 'followUp' as const,
        label: `Suggesties voor een vervolg met ${otherUserName}`,
        placeholder: 'Wat zou je graag als vervolgstap willen? Samenwerking, referral, advies?',
        required: false,
      },
      {
        key: 'outcomes' as const,
        label: 'Concrete uitkomsten en actiepunten',
        placeholder: 'Wat ga jij doen na dit gesprek? Welke afspraken zijn er gemaakt?',
        required: false,
      },
    ];

    return (
      <>
        <ToastContainer toasts={toasts} onClose={removeToast} />

        {/* On-the-way popup overlay */}
        {showOnTheWayPopup && !showCancellationInput && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 text-center mb-1">{otherUserName} is onderweg</h2>
              <p className="text-sm text-gray-500 text-center mb-6">Ben jij ook al op weg?</p>
              <div className="space-y-2">
                <button onClick={() => handleOnTheWayResponse('yes')} className="w-full py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 active:scale-[0.98] transition-all">Ja, ik ben onderweg</button>
                <button onClick={() => handleOnTheWayResponse('no')} className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 active:scale-[0.98] transition-all">Nog niet</button>
                <button onClick={() => handleOnTheWayResponse('cancel')} className="w-full py-3 text-red-600 font-medium rounded-xl hover:bg-red-50 active:scale-[0.98] transition-all">Afzeggen</button>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation popup overlay */}
        {showCancellationInput && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Afzeggen</h2>
              <p className="text-sm text-gray-500 mb-4">Geef een reden op:</p>
              <textarea value={cancellationReason} onChange={(e) => setCancellationReason(e.target.value)} placeholder="Reden..." className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/30 mb-4 min-h-[100px] resize-none" />
              <div className="flex gap-2">
                <button onClick={() => { setShowCancellationInput(false); setCancellationReason(''); }} className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all">Annuleer</button>
                <button onClick={submitCancellation} disabled={!cancellationReason.trim()} className="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-all">Bevestig</button>
              </div>
            </div>
          </div>
        )}

        {/* Maps app picker overlay */}
        {mapsPicker && (() => {
          const urls = buildMapsUrls(mapsPicker.lat, mapsPicker.lng, mapsPicker.address);
          return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[1000] p-4" role="dialog" aria-modal="true">
              <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl">
                <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 text-2xl">🗺</div>
                <h2 className="text-base font-semibold text-gray-900 text-center mb-1">Navigeer naar locatie</h2>
                <p className="text-sm text-gray-500 text-center mb-5 truncate">{mapsPicker.address}</p>
                <div className="space-y-2">
                  <a
                    href={urls.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMapsPicker(null)}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-[#4285F4] text-white font-medium rounded-xl hover:bg-[#3b77db] active:scale-[0.98] transition-all text-sm no-underline"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                    Google Maps
                  </a>
                  <a
                    href={urls.apple}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMapsPicker(null)}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gray-900 text-white font-medium rounded-xl hover:bg-gray-800 active:scale-[0.98] transition-all text-sm no-underline"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    Apple Maps
                  </a>
                  <button
                    onClick={() => setMapsPicker(null)}
                    className="w-full py-2.5 text-gray-500 text-sm font-medium rounded-xl hover:bg-gray-50 transition-all"
                  >
                    Annuleer
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Main unified flow layout */}
        <div className="w-full max-w-2xl mx-auto px-4 py-4 space-y-4">
          
          {/* Step progress bar */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-gray-900">Fysieke Afspraak</h2>
              <span className="text-xs text-gray-400">met {otherUserName}</span>
            </div>
            <div className="flex items-center gap-1">
              {flowSteps.map((step, i) => (
                <div key={step.id} className="flex items-center flex-1">
                  <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 transition-all ${
                    i < currentStepIndex ? 'bg-green-500 text-white' :
                    i === currentStepIndex ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {i < currentStepIndex ? '✓' : step.icon}
                  </div>
                  {i < flowSteps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 rounded ${i < currentStepIndex ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">{flowSteps[currentStepIndex]?.label}</p>
          </div>

          {/* Guide banner */}
          <div className="flex items-start gap-2.5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
            <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p className="text-xs text-blue-700">{stepGuides[currentFlowStep]}</p>
          </div>

          {/* Location info card (for scheduled/navigation/active) */}
          {selectedLocation && currentFlowStep !== 'voltooid' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {(currentFlowStep === 'gepland' || currentFlowStep === 'navigeer' || currentFlowStep === 'aangekomen') && (
                <EnhancedLocationMap
                  userLocations={navigationMapUserLocations}
                  selectedLocation={{ lat: selectedLocation.location.lat, lng: selectedLocation.location.lng, address: selectedLocation.location.address }}
                  routeLines={navigationMapRouteLines}
                  // Taller map during active navigation, follow current user's GPS
                  height={currentFlowStep === 'navigeer' ? 340 : 220}
                  autoFitBounds={currentFlowStep !== 'navigeer'}
                  followUserId={currentFlowStep === 'navigeer' ? currentUserId : undefined}
                />
              )}
              <div className="px-4 py-3 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">{selectedLocation.location.name || 'Ontmoetingslocatie'}</p>
                  <p className="text-xs text-gray-400 truncate">{selectedLocation.location.address}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {/* Maps buttons: choose between Google Maps or Apple Maps */}
                  {(currentFlowStep === 'navigeer' || currentFlowStep === 'gepland') && (
                    <button
                      onClick={async () => {
                        const loc = await resolveLocation();
                        if (!loc) return;
                        const { lat, lng, address, name } = loc.location;
                        openMapsPicker(lat ?? null, lng ?? null, name || address || '');
                      }}
                      className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors border border-blue-100"
                      title="Navigeer naar de locatie"
                    >
                      🗺 Navigeer
                    </button>
                  )}
                  {currentFlowStep === 'gepland' && (
                    <button
                      onClick={() => { setStatus('planning'); setSelectedLocation(null); setBestMatches([]); }}
                      className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Wijzig locatie
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Meeting active: greeting card */}
          {currentFlowStep === 'meeting' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3 animate-pulse">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" /></svg>
              </div>
              <p className="text-base font-semibold text-gray-900">Meeting is actief</p>
              <p className="text-sm text-gray-500 mt-1">Veel succes met jullie gesprek!</p>
            </div>
          )}

          {/* AI Reflection form — GEO-optimized peer review */}
          {currentFlowStep === 'ai_reflectie' && showSparQuestions && (() => {
            const EXPERTISE_TAGS = ['Strategie','Marketing','Sales','Tech & IT','Data & AI','Finance','HR & People','Legal','Design & UX','Operations','Duurzaamheid','Ondernemerschap','Leiderschap','Coaching','Netwerken'];
            const MEETING_TOPICS = ['Samenwerking verkennen','Kennisdeling','Zakelijke deal','Netwerken & connectie','Advies gevraagd','Referral/introductie','Anders'];
            const toggleTag = (tag: string) => {
              const cur = sparAnswers.expertiseTags;
              const updated = cur.includes(tag) ? cur.filter((t: string) => t !== tag) : cur.length < 5 ? [...cur, tag] : cur;
              setSparAnswers({ ...sparAnswers, expertiseTags: updated });
              if (sparErrors.expertiseTags) setSparErrors((prev: Record<string,string[]>) => { const n = {...prev}; delete n.expertiseTags; return n; });
            };
            return (
              <div className="space-y-3">
                {/* Context header */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain size={20} weight="duotone" className="text-[#7A9488] flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-gray-900">Jouw reflectie over {otherUserName}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Jouw antwoorden helpen de AI het expertprofiel van {otherUserName} op te bouwen — zichtbaar voor andere professionals en zoekmachines.</p>
                  {meetingDurationMinutes && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#7A9488]/10 rounded-full text-xs text-[#4F6660] font-medium">
                      ⏱ Meeting duur: {meetingDurationMinutes} min
                    </span>
                  )}
                </div>

                {/* 1. Expertise tags */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-3">
                    <span className="w-6 h-6 rounded-full bg-[#7A9488] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    Expertisegebieden van {otherUserName}
                    <span className="text-red-400 text-xs ml-auto">verplicht · max 5</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {EXPERTISE_TAGS.map((tag: string) => (
                      <button key={tag} onClick={() => toggleTag(tag)} type="button"
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${sparAnswers.expertiseTags.includes(tag) ? 'bg-[#7A9488] text-white border-[#7A9488]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#7A9488] hover:text-[#7A9488]'}`}>
                        {tag}
                      </button>
                    ))}
                    {/* Custom tag input */}
                    {sparAnswers.expertiseTags.length < 5 && (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={customExpertTag}
                          onChange={e => setCustomExpertTag(e.target.value)}
                          onKeyDown={e => {
                            if ((e.key === 'Enter' || e.key === ',') && customExpertTag.trim()) {
                              e.preventDefault();
                              toggleTag(customExpertTag.trim());
                              setCustomExpertTag('');
                            }
                          }}
                          placeholder="+ Eigen tag"
                          maxLength={30}
                          className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-gray-300 bg-gray-50 text-gray-500 placeholder-gray-400 focus:outline-none focus:border-[#7A9488] focus:text-gray-700 w-24 focus:w-32 transition-all"
                        />
                        {customExpertTag.trim() && (
                          <button type="button" onClick={() => { toggleTag(customExpertTag.trim()); setCustomExpertTag(''); }}
                            className="w-5 h-5 rounded-full bg-[#7A9488] text-white flex items-center justify-center text-xs flex-shrink-0">
                            +
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {sparAnswers.expertiseTags.length > 0 && (
                    <p className="mt-2 text-xs text-[#7A9488]">{sparAnswers.expertiseTags.length}/5 geselecteerd</p>
                  )}
                  {sparErrors.expertiseTags && <p className="mt-1 text-xs text-red-500">{sparErrors.expertiseTags[0]}</p>}
                </div>

                {/* 2. Expert tagline */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                    <span className="w-6 h-6 rounded-full bg-[#7A9488] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    In één zin: wat maakt {otherUserName} uniek?
                    <span className="text-red-400 text-xs ml-auto">verplicht</span>
                  </label>
                  <input type="text" maxLength={150}
                    value={sparAnswers.expertTagline}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setSparAnswers({...sparAnswers, expertTagline: e.target.value}); if (sparErrors.expertTagline) setSparErrors((p: Record<string,string[]>) => { const n={...p}; delete n.expertTagline; return n; }); }}
                    placeholder={`Bijv. "${otherUserName} combineert strategisch inzicht met een directe aanpak..."`}
                    className={`w-full px-3 py-2.5 bg-gray-50 border-0 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7A9488]/30 focus:bg-white transition-all ${sparErrors.expertTagline ? 'ring-2 ring-red-500/30 bg-red-50/50' : ''}`}
                  />
                  <div className="flex justify-between mt-1">
                    {sparErrors.expertTagline ? <p className="text-xs text-red-500">{sparErrors.expertTagline[0]}</p> : <span/>}
                    <span className="text-xs text-gray-400">{sparAnswers.expertTagline.length}/150</span>
                  </div>
                </div>

                {/* 3. Meeting topic */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                    <span className="w-6 h-6 rounded-full bg-[#7A9488] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                    Hoofdonderwerp van de meeting
                  </label>
                  <select value={sparAnswers.meetingTopic}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSparAnswers({...sparAnswers, meetingTopic: e.target.value})}
                    className="w-full px-3 py-2.5 bg-gray-50 border-0 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#7A9488]/30 focus:bg-white transition-all">
                    <option value="">Kies een onderwerp...</option>
                    {MEETING_TOPICS.map((t: string) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* 4. Recommendation */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-3">
                    <span className="w-6 h-6 rounded-full bg-[#7A9488] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                    Zou je {otherUserName} aanbevelen aan anderen?
                  </label>
                  <div className="flex gap-2 mb-2">
                    {(['yes','no'] as const).map(v => (
                      <button key={v} onClick={() => setSparAnswers({...sparAnswers, wouldRecommend: v})} type="button"
                        className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${sparAnswers.wouldRecommend === v ? (v === 'yes' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-red-400 text-white border-red-400') : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                        {v === 'yes' ? '👍 Ja, zeker' : '👎 Niet nu'}
                      </button>
                    ))}
                  </div>
                  {sparAnswers.wouldRecommend && (
                    <textarea value={sparAnswers.recommendationText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSparAnswers({...sparAnswers, recommendationText: e.target.value})}
                      placeholder={sparAnswers.wouldRecommend === 'yes' ? `Waarom zou je ${otherUserName} aanbevelen?` : 'Wat had anders gekund?'}
                      className="w-full mt-1 px-3 py-2.5 bg-gray-50 border-0 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7A9488]/30 focus:bg-white min-h-[60px] resize-none transition-all" />
                  )}
                </div>

                {/* 5-9. Open questions */}
                {sparQuestions.map((q, i) => (
                  <div key={q.key} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                    <label htmlFor={`spar-${q.key}`} className="flex items-center gap-2 text-sm font-medium text-gray-900 mb-2">
                      <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 5}</span>
                      {q.label}
                      {q.required && <span className="text-red-400 text-xs ml-auto">verplicht</span>}
                    </label>
                    <textarea id={`spar-${q.key}`}
                      value={sparAnswers[q.key as keyof typeof sparAnswers] as string}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setSparAnswers({...sparAnswers, [q.key]: e.target.value}); if (sparErrors[q.key]) setSparErrors((p: Record<string,string[]>) => { const n={...p}; delete n[q.key]; return n; }); }}
                      placeholder={q.placeholder}
                      className={`w-full px-3 py-2.5 bg-gray-50 border-0 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7A9488]/30 focus:bg-white min-h-[80px] resize-none transition-all ${sparErrors[q.key] ? 'ring-2 ring-red-500/30 bg-red-50/50' : ''}`}
                    />
                    {sparErrors[q.key] && <p className="mt-1 text-xs text-red-500">{sparErrors[q.key][0]}</p>}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Completed card */}
          {currentFlowStep === 'voltooid' && (
            <div className="space-y-3">
              {/* Main connection card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Sage gradient header met SK logo */}
                <div style={{ background: 'linear-gradient(135deg, #3C4F49 0%, #4F6660 50%, #6B8C82 100%)', padding: '28px 24px 24px', textAlign: 'center' }}>
                  <img src="/SuperKonnected_Logo.svg" alt="SuperKonnected" style={{ width: 48, height: 48, objectFit: 'contain', margin: '0 auto 14px', display: 'block', filter: 'brightness(0) invert(1) opacity(0.9)' }} />
                  <div style={{ color: '#ffffff', fontSize: 20, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
                    Nu zijn jullie SuperKonnected
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                    Jullie hebben het volledige connectieproces doorlopen
                  </div>
                </div>

                <div style={{ padding: '20px 24px' }}>
                  {/* Avatars + respect symbool */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 18 }}>
                    {/* Jouw avatar */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #6B8C82, #8DA8A0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontWeight: 800, fontSize: 20, boxShadow: '0 2px 8px rgba(107,140,130,0.3)' }}>
                        {currentUserName.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>Jij</span>
                    </div>

                    {/* Verbindingslijn + respect symbool */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 24, height: 1, background: 'linear-gradient(to right, #8DA8A0, #4F6660)' }} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                          <span style={{ fontSize: 24, color: '#4F6660', fontWeight: 300, lineHeight: 1 }}>∞</span>
                          <span style={{ fontSize: 8, color: '#7A9488', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 1 }}>respect</span>
                        </div>
                        <div style={{ width: 24, height: 1, background: 'linear-gradient(to left, #8DA8A0, #4F6660)' }} />
                      </div>
                    </div>

                    {/* Partner avatar */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #c2855a, #d49070)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontWeight: 800, fontSize: 20, boxShadow: '0 2px 8px rgba(194,133,90,0.3)' }}>
                        {otherUserName.charAt(0).toUpperCase()}
                      </div>
                      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500, maxWidth: 64, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{otherUserName}</span>
                    </div>
                  </div>

                  {/* Tekst */}
                  <p style={{ fontSize: 13, color: '#4b5563', textAlign: 'center', lineHeight: 1.6, marginBottom: 16 }}>
                    Van eerste bericht tot fysieke ontmoeting — jullie hebben samen het volledige SuperKonnected-traject doorlopen. Dit is het begin van een betekenisvolle professionele relatie.
                  </p>

                  {/* Stats */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', background: '#f9fafb', borderRadius: 14, padding: '12px 8px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#4F6660' }}>5</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>fases</div>
                    </div>
                    <div style={{ width: 1, height: 28, background: '#e5e7eb' }} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#4F6660' }}>{meetingDurationMinutes ? `${meetingDurationMinutes}m` : '—'}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>meeting</div>
                    </div>
                    <div style={{ width: 1, height: 28, background: '#e5e7eb' }} />
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#4F6660', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLocation?.location?.name || selectedLocation?.location?.address?.split(',')[0] || 'Fysiek'}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>locatie</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI note — minimaal, secondair */}
              <div style={{ background: 'rgba(122,148,136,0.07)', borderRadius: 12, padding: '10px 14px', border: '1px solid rgba(122,148,136,0.15)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Brain size={14} weight="duotone" style={{ color: '#7A9488', flexShrink: 0, marginTop: 1 }} />
                <p style={{ fontSize: 11, color: '#4F6660', lineHeight: 1.5, margin: 0 }}>
                  De AI analyseert jouw reflectie om het expertprofiel van {otherUserName} te versterken voor andere professionals en zoekmachines.
                </p>
              </div>
            </div>
          )}

          {/* === SINGLE EVOLVING CTA BUTTON === */}
          <div className="space-y-2">
            {/* Step: Gepland → Navigeer naar */}
            {currentFlowStep === 'gepland' && (
              <>
                <button onClick={startNavigation} style={{ background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)' }} className="w-full py-3.5 text-white font-semibold rounded-2xl active:scale-[0.98] transition-all shadow-md text-sm">
                  🧭 Navigeer naar locatie
                </button>
                {appointmentDateTime && (
                  <div className="text-xs text-center text-gray-500 -mt-1">
                    📅 {appointmentDateTime.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} om {appointmentDateTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
                {selectedLocation && (
                  <div className="text-center">
                    <button
                      onClick={() => setMapsPicker({ lat: selectedLocation.location.lat, lng: selectedLocation.location.lng, address: selectedLocation.location.address })}
                      className="text-xs text-gray-400 hover:text-gray-600 underline transition-all"
                    >
                      Liever Google of Apple Maps?
                    </button>
                  </div>
                )}
                <button 
                  onClick={() => { setStatus('planning'); setSelectedLocation(null); setBestMatches([]); }}
                  className="w-full py-2.5 text-gray-500 text-xs font-medium rounded-xl hover:bg-gray-50 transition-all"
                >
                  Wijzig of verplaats afspraak
                </button>
              </>
            )}
            
            {/* Step: Navigeer → Aangekomen */}
            {currentFlowStep === 'navigeer' && (
              <button onClick={verifyGPSLocation} style={{ background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)' }} className="w-full py-3.5 text-white font-semibold rounded-2xl active:scale-[0.98] transition-all shadow-lg text-sm">
                📍 Aangekomen
              </button>
            )}
            
            {/* Step: Aangekomen → Start meeting */}
            {currentFlowStep === 'aangekomen' && (
              <button onClick={startMeeting} className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-2xl hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all shadow-lg shadow-blue-500/20 text-sm">
                🤝 Start meeting
              </button>
            )}
            
            {/* Step: Meeting → Einde meeting */}
            {currentFlowStep === 'meeting' && (
              <button
                onClick={endMeeting}
                disabled={loadingStates.endingMeeting}
                className="w-full py-3.5 bg-red-600 text-white font-semibold rounded-2xl hover:bg-red-700 active:scale-[0.98] disabled:opacity-50 transition-all text-sm"
              >
                {loadingStates.endingMeeting ? 'Beëindigen...' : '⏹ Einde meeting'}
              </button>
            )}
            
            {/* Step: AI Reflectie */}
            {currentFlowStep === 'ai_reflectie' && !showSparQuestions && (
              <>
                <button
                  onClick={() => handleAIRelationManager('spar')}
                  className="w-full py-3.5 text-white font-semibold rounded-2xl active:scale-[0.98] transition-all shadow-md text-sm"
                  style={{ background: 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)', boxShadow: '0 4px 14px rgba(107,140,130,0.35)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #5E7D74 0%, #6B8579 50%, #7A9488 100%)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'linear-gradient(135deg, #6B8C82 0%, #7A9488 50%, #8DA8A0 100%)')}
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <Sparkle size={16} weight="fill" />
                    AI Relatie Manager
                  </span>
                </button>
                <button
                  onClick={() => handleAIRelationManager('skip')}
                  className="w-full py-2.5 text-xs font-medium rounded-xl transition-all border"
                  style={{ color: '#6B8C82', borderColor: '#D1DDD9', backgroundColor: 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#F0F4F3'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Overslaan → Einde flow
                </button>
              </>
            )}
            {currentFlowStep === 'ai_reflectie' && showSparQuestions && (
              <div className="flex gap-2">
                <button onClick={() => { setShowSparQuestions(false); handleAIRelationManager('skip'); }} disabled={loadingStates.submittingSpar} className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 disabled:opacity-50 transition-all text-sm">
                  Overslaan
                </button>
                <button onClick={submitSparAnswers} disabled={loadingStates.submittingSpar} className="flex-1 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all text-sm">
                  {loadingStates.submittingSpar ? 'Verzenden...' : 'Verzenden'}
                </button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
  
  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <div className="w-full max-w-xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <svg className="animate-spin w-8 h-8 text-blue-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
          <p className="text-sm text-gray-500">Laden...</p>
        </div>
      </div>
    </>
  );
};

export default React.memo(PhysicalMeetingPhaseV2);
